// Designed invitation templates the host picks on the Design step. Ids must
// match INVITE_TEMPLATES in backend/server.py — the backend coerces anything
// unknown back to 'auto'.
//
// 'auto'  — legacy behaviour: the look is derived from the event type (a
//           wedding gets the rose variant, a birthday the blush one).
// others  — a designed template: its own paper, ink, accents, fonts, artwork
//           and wax seal, applied to the same invitation layout.
//
// Each template id doubles as the variant class on InvitationPage
// (`.inv.inv-<id>`) and as the :templateId of the /templates/:templateId
// preview route the picker iframes.
//
// Two generations live side by side:
//
// - The five ORIGINAL designs (`legacy: true`) are ports of standalone HTML
//   mockups: their palette and cut-out PNG artwork are owned by hand-written
//   blocks in InvitationPage.css / InvitationPage.tsx. Nothing here repaints
//   them; the fields below only describe them for the picker and gallery.
// - Every other template is DATA-DRIVEN: `buildTemplateLook()` turns its
//   palette and title treatment into inline custom properties on `.inv`, its
//   `layout` picks one of three CSS composition families, and its `motif` +
//   `frame` place hand-drawn SVG artwork (components/invite/motifs.tsx)
//   around the viewport. A new template is one entry in this file.
//
// A second collection sits beside those hundred: the PAINTED templates
// (`collection: 'painted'`, data in paintedTemplates.ts). Their first screen
// is a painted card — artwork images with the event's words set on top by
// components/invite/PaintedCard — and they share the `painted` layout family.
// The pop-up cards (look `popup`, data in popupTemplates.ts) are painted
// templates too; PaintedCard hands them to components/invite/PopupCard.

import { INVITE_FONTS, blend, getInviteFont, isDarkColor, kaTail, luminance, shade, tint } from './inviteStyle'
import { PAINTED_DEFS } from './paintedTemplates'
import { POPUP_DEFS } from './popupTemplates'
import { paintedLookLabel } from './paintedInvite'
import type { PaintedDef, PaintedLook } from './paintedInvite'
import type { CSSProperties } from 'react'

/* ------------------------------------------------------------- vocabulary */

export type TemplateCategory =
  | 'kids'
  | 'birthday'
  | 'wedding'
  | 'baby'
  | 'corporate'
  | 'holiday'
  | 'party'
  | 'outdoor'
  | 'elegant'

/**
 * Composition family, i.e. which block of InvitationPage.css lays screen 1
 * out:
 *   classic — Storyfy's default: title first, two columns, flat schedule.
 *   card    — kicker above the wordmark, display type at poster scale,
 *             countdown and schedule in frosted cards, pill buttons.
 *   poster  — the photo IS the first screen: the wordmark lies across the
 *             cover, date bottom-left, venue bottom-right.
 *   painted — the painted card IS the first screen (painted collection).
 */
export type TemplateLayout = 'classic' | 'card' | 'poster' | 'painted'

/** The hand-coded library, or the painted artwork collection. */
export type TemplateCollection = 'classic' | 'painted'

/** Where a template's motif artwork hangs. */
export type TemplateFrame = 'corners' | 'edges' | 'garland' | 'bottom' | 'scatter' | 'none'

/** The six wax-seal photographs in src/assets. */
export type TemplateSeal = 'red' | 'gold' | 'blue' | 'navy' | 'pink' | 'sage'

export type TemplatePalette = {
  paper: string
  card: string
  ink: string
  muted: string
  a1: string
  a2: string
  gold: string
  line: string
  title: string
  btnHover: string
  btnOff: string
}

/** How the event name is set. Every field is optional; see TITLE_DEFAULTS. */
export type TitleStyle = {
  transform?: 'uppercase' | 'none'
  weight?: number
  italic?: boolean
  /** CSS letter-spacing, e.g. '0.08em'. */
  tracking?: string
  /** Degrees; the card family's calligraphy tilts a little. */
  rotate?: number
  size?: 'sm' | 'md' | 'lg' | 'xl'
  lineHeight?: number
}

export type InviteTemplate = {
  id: string
  label: { en: string; ka: string }
  /** One-line description shown under the label in the picker. */
  blurb: { en: string; ka: string }
  category: TemplateCategory
  /** Extra search words in both languages (labels and blurbs are searched too). */
  tags: string[]
  /** Swatch shown on the picker card — the template's paper + ink. */
  swatch: { paper: string; ink: string; accent: string }
  /**
   * What this design already uses. The Design step's colour, background and
   * font pickers show these as the selected option while the host has not
   * overridden anything, so the pickers always start on the template's own
   * recommendation rather than on "nothing picked". Switching template
   * re-seeds them; an explicit choice is stored on the event and wins.
   */
  defaults: { accent: string; background: string; font: string }
  /** Google Fonts stylesheet the template's type needs, if any. */
  fontHref?: string
  layout: TemplateLayout
  frame: TemplateFrame
  /** Id in components/invite/motifs.tsx; 'none' draws nothing. */
  motif: string
  seal: TemplateSeal
  palette: TemplatePalette
  /** Dark paper — the invitation flips its translucent surfaces (.inv-bg-dark). */
  dark: boolean
  title: TitleStyle
  /** Palette and artwork are owned by hand-written CSS/TSX, not by this data. */
  legacy: boolean
  collection: TemplateCollection
  /** The painted collection's artwork, colours and layout. */
  painted?: PaintedDef
}

export const TEMPLATE_CATEGORIES: Array<{
  id: TemplateCategory
  label: { en: string; ka: string }
}> = [
  { id: 'kids', label: { en: 'Kids parties', ka: 'საბავშვო' } },
  { id: 'birthday', label: { en: 'Birthday', ka: 'დაბადების დღე' } },
  { id: 'wedding', label: { en: 'Wedding & love', ka: 'ქორწილი და სიყვარული' } },
  { id: 'baby', label: { en: 'Baby & family', ka: 'ბავშვი და ოჯახი' } },
  { id: 'corporate', label: { en: 'Corporate & work', ka: 'კორპორატიული' } },
  { id: 'holiday', label: { en: 'Holidays & seasons', ka: 'დღესასწაულები და სეზონები' } },
  { id: 'party', label: { en: 'Parties & nights out', ka: 'წვეულებები' } },
  { id: 'outdoor', label: { en: 'Outdoors & travel', ka: 'ბუნება და მოგზაურობა' } },
  { id: 'elegant', label: { en: 'Elegant & minimal', ka: 'ელეგანტური და მინიმალისტური' } },
]

export const categoryLabel = (id: TemplateCategory, lang: string) => {
  const c = TEMPLATE_CATEGORIES.find((x) => x.id === id)
  return c ? (lang === 'ka' ? c.label.ka : c.label.en) : id
}

/* ----------------------------------------------------- palette derivation */

type Def = {
  id: string
  en: string
  ka: string
  blurbEn: string
  blurbKa: string
  category: TemplateCategory
  tags?: string[]
  layout: TemplateLayout
  frame: TemplateFrame
  motif: string
  seal: TemplateSeal
  font: string
  paper: string
  ink: string
  a1: string
  a2: string
  gold?: string
  /** Title colour when it is not the accent (e.g. the ink). */
  titleColor?: string
  dark?: boolean
  title?: TitleStyle
  legacy?: boolean
  /** Legacy designs keep their hand-tuned picker defaults and font bundles. */
  defaults?: { accent: string; background: string; font: string }
  fontHref?: string
  swatch?: { paper: string; ink: string; accent: string }
}

/** WCAG contrast ratio, 1 (identical) to 21. */
export const contrastRatio = (a: string, b: string) => {
  const la = luminance(a)
  const lb = luminance(b)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

/**
 * Walk `color` away from `surface` — darker on light paper, lighter on dark
 * — until it clears `min`. The hue a design was chosen for stays; only the
 * lightness moves, and only as far as it has to. Every default the
 * invitation paints type in goes through this, so a pastel accent still
 * reads as a title and a countdown digit is never lost on its own pill.
 */
const readableOn = (color: string, surface: string, min: number) => {
  const lighten = isDarkColor(surface)
  let out = color
  for (let step = 1; step <= 14 && contrastRatio(out, surface) < min; step += 1) {
    out = lighten ? tint(color, step * 0.07) : shade(color, step * 0.07)
  }
  return out
}

/**
 * Fill in the palette a template's CSS needs from the four colours a design
 * is actually chosen by — paper, ink, accent and a second accent — the way
 * the original hand-written blocks were derived, then correct every tone
 * that is set as type for contrast against what it sits on:
 *   a1   (headings, labels, button text + fill)   ≥ 4.5 on paper and card
 *   a2   (countdown digits, schedule-row labels)   ≥ 4.5 on the card
 *   title                                          ≥ 3.2 on paper
 *   muted (dates, notes, countdown labels)         ≥ 4.5 on paper and card
 * The host can still pick any colour they like — this only tunes the
 * template's own defaults.
 */
const derivePalette = (d: Def): TemplatePalette => {
  const dark = !!d.dark
  const card = dark ? tint(d.paper, 0.08) : tint(d.paper, 0.55)
  // a1 and a2 are set as SMALL type too (labels, outline-button text,
  // schedule-row labels), so they take the 4.5 bar, not the large-text 3.
  const a1 = readableOn(readableOn(d.a1, d.paper, 4.5), card, 4.5)
  // The frosted cards are the card colour at 82% over the paper, so a2 is
  // checked against both, with a little margin for the blend.
  const a2 = readableOn(readableOn(d.a2, card, 4.8), d.paper, 4.8)
  const mutedBase = dark ? blend(d.ink, d.paper, 0.4) : blend(d.ink, d.paper, 0.3)
  return {
    paper: d.paper,
    ink: d.ink,
    a1,
    a2,
    gold: d.gold || tint(d.a2, 0.3),
    card,
    // Muted is small italic type (dates, notes, countdown labels) on the paper
    // AND on the card — small text needs the 4.5 bar on both.
    muted: readableOn(readableOn(mutedBase, d.paper, 4.5), card, 4.5),
    line: dark ? tint(d.paper, 0.18) : shade(d.paper, 0.12),
    title: readableOn(d.titleColor || a1, d.paper, 3.2),
    btnHover: shade(a1, 0.2),
    btnOff: dark ? tint(d.paper, 0.28) : tint(a1, 0.6),
  }
}

const build = (d: Def): InviteTemplate => {
  const palette = derivePalette(d)
  const font = getInviteFont(d.font)
  return {
    id: d.id,
    label: { en: d.en, ka: d.ka },
    blurb: { en: d.blurbEn, ka: d.blurbKa },
    category: d.category,
    tags: d.tags || [],
    swatch: d.swatch || { paper: d.paper, ink: d.ink, accent: palette.a1 },
    defaults: d.defaults || { accent: palette.a1, background: d.paper, font: d.font },
    fontHref: d.fontHref || font?.href,
    layout: d.layout,
    frame: d.frame,
    motif: d.motif,
    seal: d.seal,
    palette,
    dark: !!d.dark,
    title: d.title || {},
    legacy: !!d.legacy,
    collection: 'classic',
  }
}

// The wax seal a painted template presses on its envelope.
const PAINTED_SEAL_MOTIF: Record<TemplateCategory, string> = {
  kids: 'star',
  birthday: 'balloon',
  wedding: 'rings',
  baby: 'moon-stars',
  corporate: 'star',
  holiday: 'star',
  party: 'balloon',
  outdoor: 'star',
  elegant: 'heart',
}

const buildPainted = (p: PaintedDef): InviteTemplate => {
  const ink = p.dark ? '#f1ece2' : p.ink
  const base = build({
    id: p.id,
    en: p.label.en,
    ka: p.label.ka,
    blurbEn: p.blurb.en,
    blurbKa: p.blurb.ka,
    category: p.category,
    tags: [...p.tags, 'painted', paintedLookLabel(p.look, 'en').toLowerCase(), paintedLookLabel(p.look, 'ka')],
    layout: 'painted',
    frame: 'none',
    motif: PAINTED_SEAL_MOTIF[p.category],
    seal: 'gold',
    font: p.font,
    paper: p.paper,
    ink,
    a1: p.accents[0],
    a2: p.accents[1] || ink,
    dark: !!p.dark,
  })
  return {
    ...base,
    swatch: { paper: p.papers?.[0] || p.paper, ink, accent: p.accents[0] },
    // The pickers compare against these, so they are the colours the card is
    // drawn in — not the page palette's contrast-corrected accent.
    defaults: {
      accent: p.accents[0],
      background: p.defaultSwatch || p.papers?.[0] || p.paper,
      font: p.font,
    },
    collection: 'painted',
    painted: p,
  }
}

/* ------------------------------------------------------------ the library */

// Ninety-five data-driven designs plus the five originals. Grouped by
// category; the order inside a category is the order the picker shows.
const DEFS: Def[] = [
  /* ---------------------------------------------------------------- kids */
  {
    id: 'rainbow-kids',
    en: 'Rainbow Kids', ka: 'ცისარტყელა',
    blurbEn: 'Bunting, a rainbow and presents — built for children’s parties.',
    blurbKa: 'ბაფთები, ცისარტყელა და საჩუქრები — საბავშვო წვეულებისთვის.',
    category: 'kids', tags: ['rainbow', 'bunting', 'presents', 'ცისარტყელა', 'ბაფთები', 'საჩუქრები'],
    layout: 'card', frame: 'none', motif: 'cloud-rainbow', seal: 'pink', font: 'baloo-delius',
    paper: '#fffdfa', ink: '#8a5a3b', a1: '#ef7fae', a2: '#e05a95', gold: '#f5c957',
    legacy: true,
    swatch: { paper: '#fffdfa', ink: '#8a5a3b', accent: '#ef7fae' },
    defaults: { accent: '#ef7fae', background: '#ffffff', font: 'baloo-delius' },
    fontHref: 'https://fonts.googleapis.com/css2?family=Baloo+2:wght@600;800&family=Delius&display=swap',
  },
  {
    id: 'dino-adventure', en: 'Dino Adventure', ka: 'დინო თავგადასავალი',
    blurbEn: 'Leafy green paper with friendly dinosaurs stomping along the edges.',
    blurbKa: 'მწვანე ქაღალდი და მეგობრული დინოზავრები კიდეებზე.',
    category: 'kids', tags: ['dinosaur', 't-rex', 'jurassic', 'დინოზავრი', 'დინო'],
    layout: 'card', frame: 'edges', motif: 'dino', seal: 'sage', font: 'fredoka',
    paper: '#edf5df', ink: '#2f4a2a', a1: '#4f9a3d', a2: '#e8a33a', gold: '#c9d87a',
    title: { weight: 700, italic: false, size: 'lg' },
  },
  {
    id: 'race-cars', en: 'Race Day', ka: 'რბოლის დღე',
    blurbEn: 'Chequered flags and racing cars in red, navy and yellow.',
    blurbKa: 'შავ-თეთრი დროშები და სარბოლო მანქანები წითელ, ლურჯ და ყვითელ ფერებში.',
    category: 'kids', tags: ['cars', 'racing', 'formula', 'მანქანები', 'რბოლა'],
    layout: 'card', frame: 'garland', motif: 'car', seal: 'red', font: 'bangers',
    paper: '#f6f3ea', ink: '#1f2a44', a1: '#d7263d', a2: '#f2b705',
    title: { transform: 'uppercase', tracking: '0.04em', weight: 400, italic: false, size: 'xl' },
  },
  {
    id: 'monster-trucks', en: 'Monster Trucks', ka: 'მონსტრ-ტრაკები',
    blurbEn: 'Big wheels, mud splashes and bold orange lettering.',
    blurbKa: 'დიდი ბორბლები, ტალახის შხეფები და მუქი ნარინჯისფერი ასოები.',
    category: 'kids', tags: ['truck', 'monster truck', 'ტრაკი', 'მანქანა'],
    layout: 'card', frame: 'edges', motif: 'truck', seal: 'gold', font: 'bangers',
    paper: '#f3efe4', ink: '#2b2b2b', a1: '#f26b1d', a2: '#3c8dbc',
    title: { transform: 'uppercase', tracking: '0.03em', weight: 400, italic: false, size: 'xl' },
  },
  {
    id: 'space-explorer', en: 'Space Explorer', ka: 'კოსმოსის მკვლევარი',
    blurbEn: 'Midnight blue, rockets and planets, glowing lettering.',
    blurbKa: 'შუაღამის ლურჯი, რაკეტები და პლანეტები, მანათობელი ასოები.',
    category: 'kids', tags: ['space', 'rocket', 'galaxy', 'astronaut', 'კოსმოსი', 'რაკეტა'],
    layout: 'card', frame: 'scatter', motif: 'rocket', seal: 'navy', font: 'orbitron',
    paper: '#0f1733', ink: '#e8ecff', a1: '#7c9cff', a2: '#ffcf5c', gold: '#b9c6ff', dark: true,
    title: { transform: 'uppercase', weight: 700, tracking: '0.06em', italic: false, size: 'md' },
  },
  {
    id: 'magical-unicorn', en: 'Magical Unicorn', ka: 'ჯადოსნური უნიკორნი',
    blurbEn: 'Pastel pink and sky blue with unicorns and stars.',
    blurbKa: 'პასტელური ვარდისფერი და ცისფერი, უნიკორნები და ვარსკვლავები.',
    category: 'kids', tags: ['unicorn', 'magic', 'pastel', 'უნიკორნი', 'ჯადო'],
    layout: 'card', frame: 'corners', motif: 'unicorn', seal: 'pink', font: 'pacifico',
    paper: '#fdf0f8', ink: '#6b3f78', a1: '#e56ab3', a2: '#7cc7ea', gold: '#f4d36b',
    title: { italic: false, weight: 400, size: 'lg' },
  },
  {
    id: 'royal-princess', en: 'Royal Princess', ka: 'სამეფო პრინცესა',
    blurbEn: 'Crowns and fairytale calligraphy in blush and gold.',
    blurbKa: 'გვირგვინები და ზღაპრული კალიგრაფია ვარდისფერსა და ოქროსფერში.',
    category: 'kids', tags: ['princess', 'crown', 'castle', 'პრინცესა', 'გვირგვინი'],
    layout: 'classic', frame: 'corners', motif: 'crown', seal: 'pink', font: 'parisienne',
    paper: '#fff4f7', ink: '#6f4460', a1: '#d64f8f', a2: '#e2bd5c', gold: '#e6c265',
    title: { italic: false, size: 'lg', lineHeight: 1.3 },
  },
  {
    id: 'fairy-garden', en: 'Fairy Garden', ka: 'ფერიების ბაღი',
    blurbEn: 'Soft greens, wands and sparkles scattered like fairy dust.',
    blurbKa: 'რბილი მწვანე, ჯადოსნური ჯოხები და ფერიების ნაპერწკლები.',
    category: 'kids', tags: ['fairy', 'enchanted', 'garden', 'ფერია', 'ბაღი'],
    layout: 'card', frame: 'scatter', motif: 'wand', seal: 'sage', font: 'caveat',
    paper: '#f2f8ee', ink: '#3f5a3a', a1: '#7fb35f', a2: '#ec8fb4', gold: '#f2d16b',
    title: { weight: 700, italic: false, size: 'xl' },
  },
  {
    id: 'under-the-sea', en: 'Under the Sea', ka: 'წყალქვეშა სამყარო',
    blurbEn: 'Teal water, coral fish and rising bubbles.',
    blurbKa: 'ფირუზისფერი წყალი, მარჯნისფერი თევზები და ბუშტუკები.',
    category: 'kids', tags: ['sea', 'ocean', 'fish', 'ზღვა', 'თევზი', 'ოკეანე'],
    layout: 'card', frame: 'edges', motif: 'fish', seal: 'blue', font: 'fredoka',
    paper: '#e4f4f7', ink: '#1f4e5f', a1: '#2aa7b8', a2: '#ff8c69', gold: '#8fd8e2',
    title: { weight: 700, italic: false, size: 'lg' },
  },
  {
    id: 'pirate-treasure', en: 'Pirate Treasure', ka: 'მეკობრეთა განძი',
    blurbEn: 'Parchment paper, a pirate ship and gold coins.',
    blurbKa: 'პერგამენტის ქაღალდი, მეკობრეთა გემი და ოქროს მონეტები.',
    category: 'kids', tags: ['pirate', 'ship', 'treasure', 'მეკობრე', 'გემი', 'განძი'],
    layout: 'card', frame: 'edges', motif: 'ship', seal: 'red', font: 'luckiest-guy',
    paper: '#f4ead8', ink: '#3b2a1e', a1: '#8b1e1e', a2: '#d4a017',
    title: { italic: false, weight: 400, size: 'lg', tracking: '0.02em' },
  },
  {
    id: 'superhero-squad', en: 'Superhero Squad', ka: 'სუპერგმირები',
    blurbEn: 'Comic-book red, blue and yellow with lightning bolts.',
    blurbKa: 'კომიქსური წითელი, ლურჯი და ყვითელი ელვებით.',
    category: 'kids', tags: ['superhero', 'comic', 'hero', 'სუპერგმირი', 'კომიქსი'],
    layout: 'card', frame: 'garland', motif: 'bolt', seal: 'navy', font: 'bangers',
    paper: '#fff7e0', ink: '#1b2a5a', a1: '#e0322b', a2: '#ffc107',
    title: { transform: 'uppercase', size: 'xl', italic: false, weight: 400, tracking: '0.03em' },
  },
  {
    id: 'safari-adventure', en: 'Safari Adventure', ka: 'საფარი',
    blurbEn: 'Sand and khaki with giraffes and acacia leaves.',
    blurbKa: 'ქვიშისფერი და ხაკი, ჟირაფები და აკაციის ფოთლები.',
    category: 'kids', tags: ['safari', 'giraffe', 'animals', 'jungle', 'საფარი', 'ჟირაფი', 'ცხოველები'],
    layout: 'card', frame: 'edges', motif: 'giraffe', seal: 'gold', font: 'fredoka',
    paper: '#f6efdf', ink: '#4a3a25', a1: '#c98a3c', a2: '#6f8f4e', gold: '#e2c48a',
    title: { weight: 700, italic: false, size: 'lg' },
  },
  {
    id: 'jungle-party', en: 'Jungle Party', ka: 'ჯუნგლების წვეულება',
    blurbEn: 'Monstera leaves and tropical green with a splash of orange.',
    blurbKa: 'მონსტერას ფოთლები და ტროპიკული მწვანე ნარინჯისფერი შხეფით.',
    category: 'kids', tags: ['jungle', 'tropical', 'leaves', 'ჯუნგლები', 'ტროპიკული'],
    layout: 'classic', frame: 'corners', motif: 'monstera', seal: 'sage', font: 'luckiest-guy',
    paper: '#e8f2e2', ink: '#244a2c', a1: '#2f8f4d', a2: '#f2a541',
    title: { italic: false, weight: 400, size: 'lg' },
  },
  {
    id: 'farm-friends', en: 'Farm Friends', ka: 'ფერმის მეგობრები',
    blurbEn: 'A red barn, a tractor and hay-coloured paper.',
    blurbKa: 'წითელი ბეღელი, ტრაქტორი და თივისფერი ქაღალდი.',
    category: 'kids', tags: ['farm', 'barn', 'tractor', 'animals', 'ფერმა', 'ტრაქტორი'],
    layout: 'card', frame: 'edges', motif: 'barn', seal: 'red', font: 'chewy',
    paper: '#fbf3e4', ink: '#5b3a21', a1: '#d9534f', a2: '#7cb342', gold: '#f0c75e',
    title: { italic: false, weight: 400, size: 'lg' },
  },
  {
    id: 'construction-zone', en: 'Construction Zone', ka: 'სამშენებლო ზონა',
    blurbEn: 'Caution-stripe yellow and black with diggers on site.',
    blurbKa: 'ყვითელ-შავი გამაფრთხილებელი ზოლები და ექსკავატორები.',
    category: 'kids', tags: ['construction', 'digger', 'excavator', 'trucks', 'მშენებლობა', 'ექსკავატორი'],
    layout: 'card', frame: 'garland', motif: 'excavator', seal: 'gold', font: 'bangers',
    paper: '#fff7d6', ink: '#2a2a2a', a1: '#f5a623', a2: '#3a3a3a', gold: '#ffd76a',
    title: { transform: 'uppercase', size: 'xl', italic: false, weight: 400, tracking: '0.04em' },
  },
  {
    id: 'choo-choo-train', en: 'Choo-Choo Train', ka: 'მატარებელი',
    blurbEn: 'A little steam train chugging along the bottom of the page.',
    blurbKa: 'პატარა ორთქლის მატარებელი, რომელიც გვერდის ბოლოს მიქრის.',
    category: 'kids', tags: ['train', 'railway', 'მატარებელი', 'რკინიგზა'],
    layout: 'card', frame: 'bottom', motif: 'train', seal: 'blue', font: 'fredoka',
    paper: '#eef3fa', ink: '#23395b', a1: '#d94f3d', a2: '#4a7bd0', gold: '#f2c14e',
    title: { weight: 700, italic: false, size: 'lg' },
  },
  {
    id: 'cloud-nine', en: 'Cloud Nine', ka: 'ღრუბლებზე',
    blurbEn: 'Dreamy lilac, fluffy clouds and a pastel rainbow.',
    blurbKa: 'ოცნებისეული იასამნისფერი, ფაფუკი ღრუბლები და პასტელური ცისარტყელა.',
    category: 'kids', tags: ['clouds', 'pastel', 'rainbow', 'dreamy', 'ღრუბლები', 'პასტელი'],
    layout: 'card', frame: 'scatter', motif: 'cloud-rainbow', seal: 'pink', font: 'quicksand',
    paper: '#f7f4ff', ink: '#5a4a86', a1: '#b28dff', a2: '#ffb3c6', gold: '#ffe08a',
    title: { weight: 700, italic: false, size: 'lg' },
  },
  {
    id: 'teddy-picnic', en: 'Teddy Bear Picnic', ka: 'დათუნიას პიკნიკი',
    blurbEn: 'Honey-brown teddies on warm cream paper.',
    blurbKa: 'თაფლისფერი დათუნიები თბილ კრემისფერ ქაღალდზე.',
    category: 'kids', tags: ['teddy', 'bear', 'picnic', 'დათუნია', 'პიკნიკი'],
    layout: 'classic', frame: 'corners', motif: 'teddy', seal: 'gold', font: 'chewy',
    paper: '#fbf1e6', ink: '#5a3d2b', a1: '#b97b52', a2: '#e5a0a8', gold: '#e9c99a',
    title: { italic: false, weight: 400, size: 'lg' },
  },
  {
    id: 'little-ballerina', en: 'Little Ballerina', ka: 'პატარა ბალერინა',
    blurbEn: 'Ballet slippers and ribbons in ballet pink.',
    blurbKa: 'პუანტები და ბაფთები ბალეტის ვარდისფერში.',
    category: 'kids', tags: ['ballet', 'ballerina', 'dance', 'ბალეტი', 'ცეკვა'],
    layout: 'classic', frame: 'corners', motif: 'ballet', seal: 'pink', font: 'parisienne',
    paper: '#fff5f8', ink: '#7d5a68', a1: '#e39bb5', a2: '#cdb47a',
    title: { italic: false, size: 'lg', lineHeight: 1.3 },
  },
  {
    id: 'level-up', en: 'Level Up', ka: 'ახალი დონე',
    blurbEn: 'Pixel type and neon green on a dark arcade screen.',
    blurbKa: 'პიქსელური შრიფტი და ნეონის მწვანე მუქ ეკრანზე.',
    category: 'kids', tags: ['gaming', 'video games', 'arcade', 'pixel', 'თამაშები', 'გეიმინგი'],
    layout: 'card', frame: 'scatter', motif: 'controller', seal: 'navy', font: 'press-start',
    paper: '#16162a', ink: '#e8ffe9', a1: '#39ff88', a2: '#ff4fd8', gold: '#ffe45c', dark: true,
    title: { transform: 'uppercase', size: 'sm', italic: false, weight: 400, lineHeight: 1.35 },
  },
  {
    id: 'butterfly-garden', en: 'Butterfly Garden', ka: 'პეპლების ბაღი',
    blurbEn: 'Violet and amber butterflies drifting over meadow paper.',
    blurbKa: 'იისფერი და ქარვისფერი პეპლები მინდვრის ქაღალდზე.',
    category: 'kids', tags: ['butterfly', 'garden', 'spring', 'პეპელა', 'ბაღი'],
    layout: 'classic', frame: 'scatter', motif: 'butterfly', seal: 'pink', font: 'caveat',
    paper: '#f5f7ef', ink: '#4a5a3a', a1: '#9b6bd6', a2: '#f4b942', gold: '#cbe07a',
    title: { weight: 700, italic: false, size: 'xl' },
  },
  {
    id: 'big-top-circus', en: 'Big Top Circus', ka: 'ცირკი',
    blurbEn: 'Striped tents and carnival bunting in red and gold.',
    blurbKa: 'ზოლიანი კარვები და კარნავალის ბაფთები წითელსა და ოქროსფერში.',
    category: 'kids', tags: ['circus', 'carnival', 'tent', 'ცირკი', 'კარნავალი'],
    layout: 'card', frame: 'garland', motif: 'circus', seal: 'red', font: 'bangers',
    paper: '#fff5e6', ink: '#8a1c1c', a1: '#d7263d', a2: '#f2b705', gold: '#f7d67a',
    title: { transform: 'uppercase', size: 'xl', italic: false, weight: 400, tracking: '0.04em' },
  },

  /* ------------------------------------------------------------ birthday */
  {
    id: 'blue-script',
    en: 'Blue Script', ka: 'ლურჯი ხელნაწერი',
    blurbEn: 'Ink-blue calligraphy on cream paper, balloons and a cake.',
    blurbKa: 'ლურჯი კალიგრაფია კრემისფერ ქაღალდზე, ბუშტებით და ტორტით.',
    category: 'birthday', tags: ['calligraphy', 'balloons', 'cake', 'კალიგრაფია', 'ბუშტები', 'ტორტი'],
    layout: 'card', frame: 'none', motif: 'balloon', seal: 'blue', font: 'pinyon-bricolage',
    paper: '#f1ecdc', ink: '#2f45c0', a1: '#5c70d6', a2: '#24379c', gold: '#8fa0e8',
    legacy: true,
    swatch: { paper: '#f1ecdc', ink: '#2f45c0', accent: '#5c70d6' },
    defaults: { accent: '#2f45c0', background: '#f1ecdc', font: 'pinyon-bricolage' },
    fontHref: 'https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@400;500;600;700&family=Pinyon+Script&display=swap',
  },
  {
    id: 'retro-festive',
    en: 'Retro Festive', ka: 'რეტრო',
    blurbEn: 'Cream paper, bunting and balloons in retro blue and red.',
    blurbKa: 'კრემისფერი ქაღალდი, ბაფთები და ბუშტები რეტრო ლურჯსა და წითელში.',
    category: 'birthday', tags: ['retro', 'bunting', 'balloons', 'რეტრო', 'ბაფთები'],
    layout: 'card', frame: 'none', motif: 'balloon', seal: 'red', font: 'glass-pompiere',
    paper: '#fff5e4', ink: '#3d55a5', a1: '#d94f3d', a2: '#c23e2e', gold: '#e8846f',
    legacy: true,
    swatch: { paper: '#fff5e4', ink: '#3d55a5', accent: '#d94f3d' },
    defaults: { accent: '#d94f3d', background: '#fff5e4', font: 'glass-pompiere' },
    fontHref: 'https://fonts.googleapis.com/css2?family=Glass+Antiqua&family=Pompiere&family=Cedarville+Cursive&display=swap',
  },
  {
    id: 'candy-land', en: 'Candy Land', ka: 'ტკბილეულის ქვეყანა',
    blurbEn: 'Lollipops and sweets in bubblegum pink and sky blue.',
    blurbKa: 'შაქარყინულები და ტკბილეული ვარდისფერსა და ცისფერში.',
    category: 'birthday', tags: ['candy', 'sweets', 'lollipop', 'კანფეტი', 'ტკბილეული'],
    layout: 'card', frame: 'garland', motif: 'candy', seal: 'pink', font: 'chewy',
    paper: '#fff0f5', ink: '#8c3a63', a1: '#ff5fa2', a2: '#59c3e8', gold: '#ffd166',
    title: { italic: false, weight: 400, size: 'lg' },
  },
  {
    id: 'ice-cream-social', en: 'Ice Cream Social', ka: 'ნაყინის წვეულება',
    blurbEn: 'Strawberry, mint and vanilla scoops along the bottom.',
    blurbKa: 'მარწყვის, პიტნისა და ვანილის ნაყინი გვერდის ბოლოს.',
    category: 'birthday', tags: ['ice cream', 'summer', 'sweets', 'ნაყინი', 'ზაფხული'],
    layout: 'card', frame: 'bottom', motif: 'icecream', seal: 'pink', font: 'fredoka',
    paper: '#fff7ef', ink: '#7a4a4a', a1: '#ff8fa3', a2: '#8fd6c8', gold: '#ffd27f',
    title: { weight: 700, italic: false, size: 'lg' },
  },
  {
    id: 'pizza-party', en: 'Pizza Party', ka: 'პიცის წვეულება',
    blurbEn: 'Tomato red and cheese yellow with pizza slices in the corners.',
    blurbKa: 'პომიდვრისფერი წითელი და ყველისფერი ყვითელი, პიცის ნაჭრები კუთხეებში.',
    category: 'birthday', tags: ['pizza', 'food', 'პიცა', 'საჭმელი'],
    layout: 'card', frame: 'corners', motif: 'pizza', seal: 'red', font: 'luckiest-guy',
    paper: '#fff4e0', ink: '#5a2d0c', a1: '#d7412a', a2: '#f2b134', gold: '#7cb342',
    title: { italic: false, weight: 400, size: 'lg' },
  },
  {
    id: 'soccer-champions', en: 'Soccer Champions', ka: 'ფეხბურთის ჩემპიონები',
    blurbEn: 'Pitch green, footballs and a championship banner.',
    blurbKa: 'მოედნის მწვანე, ბურთები და საჩემპიონო ბანერი.',
    category: 'birthday', tags: ['soccer', 'football', 'sport', 'world cup', 'ფეხბურთი', 'სპორტი'],
    layout: 'card', frame: 'garland', motif: 'football', seal: 'sage', font: 'bangers',
    paper: '#eef8ee', ink: '#1e3d2b', a1: '#2e8b57', a2: '#1c1c1c', gold: '#cfe6c9',
    title: { transform: 'uppercase', size: 'xl', italic: false, weight: 400, tracking: '0.03em' },
  },
  {
    id: 'art-studio', en: 'Art Studio', ka: 'სახელოსნო',
    blurbEn: 'Paint splashes and brushes — for creative parties.',
    blurbKa: 'საღებავის შხეფები და ფუნჯები — შემოქმედებითი წვეულებისთვის.',
    category: 'birthday', tags: ['art', 'paint', 'craft', 'ხელოვნება', 'ხატვა'],
    layout: 'card', frame: 'scatter', motif: 'palette', seal: 'red', font: 'chewy',
    paper: '#fffdf7', ink: '#333333', a1: '#ff5a5f', a2: '#3f88c5', gold: '#f7c948',
    title: { italic: false, weight: 400, size: 'lg' },
  },
  {
    id: 'science-lab', en: 'Science Lab', ka: 'ლაბორატორია',
    blurbEn: 'Bubbling flasks and atoms in lab green and violet.',
    blurbKa: 'მდუღარე კოლბები და ატომები ლაბორატორიულ მწვანესა და იისფერში.',
    category: 'birthday', tags: ['science', 'lab', 'experiment', 'მეცნიერება', 'ექსპერიმენტი'],
    layout: 'card', frame: 'corners', motif: 'flask', seal: 'sage', font: 'quicksand',
    paper: '#eefaf3', ink: '#1d3b2f', a1: '#22b573', a2: '#9b5de5', gold: '#7de3b5',
    title: { weight: 700, italic: false, size: 'lg' },
  },
  {
    id: 'slumber-party', en: 'Slumber Party', ka: 'პიჟამების წვეულება',
    blurbEn: 'Night-sky purple with moons and stars for sleepovers.',
    blurbKa: 'ღამის ცის იისფერი, მთვარეები და ვარსკვლავები ღამისთევისთვის.',
    category: 'birthday', tags: ['sleepover', 'pyjama', 'night', 'ღამისთევა', 'პიჟამა'],
    layout: 'classic', frame: 'scatter', motif: 'moon-stars', seal: 'navy', font: 'quicksand',
    paper: '#2b2452', ink: '#f0eaff', a1: '#b9a3ff', a2: '#ffd670', gold: '#d9ccff', dark: true,
    title: { weight: 700, italic: false, size: 'lg' },
  },
  {
    id: 'golden-glam', en: 'Golden Glam', ka: 'ოქროს ბრწყინვალება',
    blurbEn: 'Black and gold with sparkles — a grown-up birthday gala.',
    blurbKa: 'შავი და ოქრო ნაპერწკლებით — ზრდასრულთა საიუბილეო საღამო.',
    category: 'birthday', tags: ['gold', 'black', 'glam', 'gala', 'elegant', 'ოქრო', 'შავი', 'გალა'],
    layout: 'poster', frame: 'corners', motif: 'sparkle', seal: 'gold', font: 'cinzel',
    paper: '#141210', ink: '#f6e9c9', a1: '#d4a94a', a2: '#f0d58a', gold: '#e8c775', dark: true,
    title: { transform: 'uppercase', weight: 500, tracking: '0.02em', lineHeight: 0.95 },
  },
  {
    id: 'neon-disco', en: 'Neon Disco', ka: 'ნეონ დისკო',
    blurbEn: 'Hot pink and cyan neon on black, disco balls spinning.',
    blurbKa: 'ვარდისფერი და ცისფერი ნეონი შავზე, მბრუნავი დისკო-ბურთები.',
    category: 'birthday', tags: ['disco', 'neon', 'dance', 'party', 'დისკო', 'ნეონი', 'ცეკვა'],
    layout: 'card', frame: 'scatter', motif: 'disco', seal: 'pink', font: 'bebas',
    paper: '#120b24', ink: '#f6f0ff', a1: '#ff3cac', a2: '#3cf6ff', gold: '#ffe35c', dark: true,
    title: { transform: 'uppercase', size: 'xl', italic: false, weight: 400, tracking: '0.06em' },
  },
  {
    id: 'groovy-seventies', en: 'Groovy Seventies', ka: 'რეტრო 70-იანები',
    blurbEn: 'Mustard, rust and flower power — a seventies throwback.',
    blurbKa: 'მდოგვისფერი, ჟანგისფერი და ყვავილები — 70-იანების სტილი.',
    category: 'birthday', tags: ['70s', 'retro', 'groovy', 'vintage', 'რეტრო', 'ვინტაჟი'],
    layout: 'card', frame: 'corners', motif: 'groovy', seal: 'gold', font: 'lobster',
    paper: '#fbe9c6', ink: '#5a2e12', a1: '#e2711d', a2: '#b5451b', gold: '#f2b632',
    title: { italic: false, weight: 400, size: 'lg' },
  },
  {
    id: 'milestone-birthday', en: 'Milestone Birthday', ka: 'საიუბილეო დაბადების დღე',
    blurbEn: 'Big editorial type and gold poppers — for 30, 40, 50 and beyond.',
    blurbKa: 'დიდი ჟურნალური შრიფტი და ოქროს კონფეტი — 30, 40, 50 და მეტი წლისთვის.',
    category: 'birthday', tags: ['30', '40', '50', 'milestone', 'jubilee', 'იუბილე', 'წლისთავი'],
    layout: 'card', frame: 'edges', motif: 'popper', seal: 'gold', font: 'abril',
    paper: '#f7f4ee', ink: '#1b1b1b', a1: '#c9a24b', a2: '#1b1b1b', gold: '#e6cf8b', titleColor: '#1b1b1b',
    title: { size: 'xl', italic: false, weight: 400, lineHeight: 0.95 },
  },
  {
    id: 'balloon-pop', en: 'Balloon Pop', ka: 'ბუშტების ზეიმი',
    blurbEn: 'A bunch of pink, blue and yellow balloons across the top.',
    blurbKa: 'ვარდისფერი, ლურჯი და ყვითელი ბუშტების თაიგული ზემოთ.',
    category: 'birthday', tags: ['balloons', 'confetti', 'ბუშტები', 'კონფეტი'],
    layout: 'card', frame: 'garland', motif: 'balloon', seal: 'pink', font: 'fredoka',
    paper: '#fff8f2', ink: '#3b2f4a', a1: '#ff6f91', a2: '#6fb1ff', gold: '#ffd166',
    title: { weight: 700, italic: false, size: 'lg' },
  },

  /* ------------------------------------------------------------- wedding */
  {
    id: 'ivory-classic', en: 'Ivory Classic', ka: 'სპილოსძვლის კლასიკა',
    blurbEn: 'Ivory paper, interlocked rings and a quiet serif.',
    blurbKa: 'სპილოსძვლისფერი ქაღალდი, გადაჯაჭვული ბეჭდები და წყნარი სერიფი.',
    category: 'wedding', tags: ['classic', 'ivory', 'rings', 'traditional', 'კლასიკური', 'ბეჭდები'],
    layout: 'classic', frame: 'corners', motif: 'rings', seal: 'gold', font: 'cormorant',
    paper: '#fbf8f1', ink: '#4a4238', a1: '#a68a5c', a2: '#7a6b52', gold: '#cdb98a',
    title: { italic: true, weight: 500, size: 'md' },
  },
  {
    id: 'botanical-greenery', en: 'Botanical Greenery', ka: 'ბოტანიკური სიმწვანე',
    blurbEn: 'Eucalyptus sprigs in sage over soft white.',
    blurbKa: 'ევკალიპტის ტოტები ნაცრისფერ-მწვანეში რბილ თეთრზე.',
    category: 'wedding', tags: ['greenery', 'eucalyptus', 'botanical', 'sage', 'ევკალიპტი', 'მწვანე'],
    layout: 'classic', frame: 'corners', motif: 'eucalyptus', seal: 'sage', font: 'cormorant',
    paper: '#f4f6ef', ink: '#3d4a3a', a1: '#6e8f62', a2: '#9fb58e', gold: '#c7b27a',
    title: { italic: true, weight: 500, size: 'md' },
  },
  {
    id: 'blush-roses', en: 'Blush Roses', ka: 'ვარდისფერი ვარდები',
    blurbEn: 'Hand-drawn roses and romantic calligraphy in blush.',
    blurbKa: 'ხელნახატი ვარდები და რომანტიული კალიგრაფია ვარდისფერში.',
    category: 'wedding', tags: ['roses', 'romantic', 'blush', 'floral', 'ვარდები', 'რომანტიული'],
    layout: 'classic', frame: 'corners', motif: 'rose', seal: 'pink', font: 'great-vibes',
    paper: '#fbf1f1', ink: '#5c3f45', a1: '#c97b8a', a2: '#e2a9b1', gold: '#d8b98a',
    title: { italic: false, weight: 400, size: 'lg', lineHeight: 1.3 },
  },
  {
    id: 'navy-gold', en: 'Navy & Gold', ka: 'ლურჯი და ოქრო',
    blurbEn: 'Deep navy paper with gold flourishes and capitals.',
    blurbKa: 'მუქი ლურჯი ქაღალდი ოქროს ორნამენტებითა და მთავრული ასოებით.',
    category: 'wedding', tags: ['navy', 'gold', 'formal', 'ლურჯი', 'ოქრო', 'ფორმალური'],
    layout: 'classic', frame: 'corners', motif: 'flourish', seal: 'navy', font: 'cinzel',
    paper: '#1b2a4a', ink: '#f4efe4', a1: '#d4b46a', a2: '#efd9a2', gold: '#e6c987', dark: true,
    title: { transform: 'uppercase', tracking: '0.1em', weight: 500, italic: false, size: 'md' },
  },
  {
    id: 'rustic-kraft', en: 'Rustic Kraft', ka: 'რუსტიკული',
    blurbEn: 'Kraft paper, wheat and handwriting — a barn wedding.',
    blurbKa: 'კრაფტის ქაღალდი, ხორბალი და ხელნაწერი — სოფლური ქორწილი.',
    category: 'wedding', tags: ['rustic', 'kraft', 'barn', 'wheat', 'რუსტიკული', 'სოფლური'],
    layout: 'classic', frame: 'corners', motif: 'wheat', seal: 'gold', font: 'caveat',
    paper: '#e9d9bf', ink: '#4a3a2a', a1: '#8a5a3a', a2: '#6f7b4f', gold: '#c9a36b',
    title: { weight: 700, italic: false, size: 'xl' },
  },
  {
    id: 'boho-terracotta', en: 'Boho Terracotta', ka: 'ბოჰო ტერაკოტა',
    blurbEn: 'Terracotta arches and a desert sun on sand.',
    blurbKa: 'ტერაკოტის თაღები და უდაბნოს მზე ქვიშისფერზე.',
    category: 'wedding', tags: ['boho', 'terracotta', 'arch', 'desert', 'ბოჰო', 'ტერაკოტა'],
    layout: 'classic', frame: 'edges', motif: 'arch', seal: 'gold', font: 'abril',
    paper: '#f6e9dd', ink: '#5a3d31', a1: '#c3603c', a2: '#d9a066', gold: '#e2c49b',
    title: { italic: false, weight: 400, size: 'lg' },
  },
  {
    id: 'art-deco', en: 'Art Deco', ka: 'არტ დეკო',
    blurbEn: 'Gatsby black and gold with fan motifs.',
    blurbKa: 'გეტსბის შავი და ოქრო, მარაოს ორნამენტებით.',
    category: 'wedding', tags: ['art deco', 'gatsby', 'twenties', 'gold', 'გეტსბი', '20-იანები'],
    layout: 'classic', frame: 'corners', motif: 'deco-fan', seal: 'gold', font: 'cinzel',
    paper: '#101418', ink: '#f2e7c8', a1: '#cfae62', a2: '#e9d39a', gold: '#e0c37f', dark: true,
    title: { transform: 'uppercase', tracking: '0.14em', weight: 600, italic: false, size: 'md' },
  },
  {
    id: 'vineyard', en: 'Vineyard', ka: 'ვენახი',
    blurbEn: 'Grapevines in wine and olive for a winery wedding.',
    blurbKa: 'ვაზი ღვინისფერსა და ზეთისხილისფერში — მარნის ქორწილისთვის.',
    category: 'wedding', tags: ['vineyard', 'wine', 'grapes', 'winery', 'ვენახი', 'ღვინო', 'ყურძენი'],
    layout: 'classic', frame: 'corners', motif: 'grapes', seal: 'red', font: 'playfair-montserrat',
    paper: '#f6f1ea', ink: '#4a2e3a', a1: '#7b2d4a', a2: '#8a9a5b', gold: '#c9b27a',
    title: { italic: true, weight: 400, size: 'md' },
  },
  {
    id: 'beach-wedding', en: 'Beach Wedding', ka: 'ზღვისპირა ქორწილი',
    blurbEn: 'Sea-glass blue, waves and shells on sand-white paper.',
    blurbKa: 'ზღვის ცისფერი, ტალღები და ნიჟარები ქვიშისფერ-თეთრ ქაღალდზე.',
    category: 'wedding', tags: ['beach', 'sea', 'shells', 'destination', 'ზღვა', 'სანაპირო'],
    layout: 'classic', frame: 'edges', motif: 'wave-shell', seal: 'blue', font: 'great-vibes',
    paper: '#f4f7f5', ink: '#2e4a55', a1: '#3b8ea5', a2: '#e2c29a', gold: '#d8c39a',
    title: { italic: false, weight: 400, size: 'lg', lineHeight: 1.3 },
  },
  {
    id: 'modern-minimal', en: 'Modern Minimal', ka: 'თანამედროვე მინიმალიზმი',
    blurbEn: 'Black type on white, a single line-drawn leaf, nothing else.',
    blurbKa: 'შავი შრიფტი თეთრზე, ერთი ხაზოვანი ფოთოლი და მეტი არაფერი.',
    category: 'wedding', tags: ['minimal', 'modern', 'black and white', 'მინიმალისტური', 'თანამედროვე'],
    layout: 'classic', frame: 'corners', motif: 'line-leaf', seal: 'sage', font: 'dmserif-inter',
    paper: '#ffffff', ink: '#1a1a1a', a1: '#1a1a1a', a2: '#8a8a8a', gold: '#cfcfcf',
    title: { italic: false, weight: 400, size: 'lg', tracking: '-0.01em' },
  },
  {
    id: 'lavender-fields', en: 'Lavender Fields', ka: 'ლავანდის ველები',
    blurbEn: 'Lavender sprigs and lilac script for a Provence feel.',
    blurbKa: 'ლავანდის ტოტები და იასამნისფერი ხელნაწერი — პროვანსის განწყობა.',
    category: 'wedding', tags: ['lavender', 'lilac', 'provence', 'ლავანდა', 'იასამნისფერი'],
    layout: 'classic', frame: 'corners', motif: 'lavender', seal: 'navy', font: 'parisienne',
    paper: '#f5f1fa', ink: '#4a3f66', a1: '#8d74b8', a2: '#b9a4d9', gold: '#d7c69a',
    title: { italic: false, size: 'lg', lineHeight: 1.3 },
  },
  {
    id: 'engagement-sparkle', en: 'Engagement Sparkle', ka: 'ნიშნობა',
    blurbEn: 'Diamonds and sparkles on rose-white — she said yes.',
    blurbKa: 'ბრილიანტები და ნაპერწკლები ვარდისფერ-თეთრზე — მან თქვა „კი“.',
    category: 'wedding', tags: ['engagement', 'diamond', 'proposal', 'ნიშნობა', 'ბრილიანტი'],
    layout: 'classic', frame: 'scatter', motif: 'diamond', seal: 'pink', font: 'great-vibes',
    paper: '#fdf7f4', ink: '#5a4046', a1: '#c8607a', a2: '#d9b36b', gold: '#e9cf8c',
    title: { italic: false, weight: 400, size: 'lg', lineHeight: 1.3 },
  },
  {
    id: 'golden-anniversary', en: 'Golden Anniversary', ka: 'ოქროს იუბილე',
    blurbEn: 'Warm gold rings and serif for anniversaries.',
    blurbKa: 'თბილი ოქროს ბეჭდები და სერიფი — ქორწინების იუბილესთვის.',
    category: 'wedding', tags: ['anniversary', 'gold', 'rings', 'იუბილე', 'ოქრო'],
    layout: 'classic', frame: 'corners', motif: 'rings', seal: 'gold', font: 'cormorant',
    paper: '#fbf6ea', ink: '#4a3d2a', a1: '#b8913f', a2: '#d9bb74', gold: '#e6cd8f',
    title: { italic: true, weight: 500, size: 'md' },
  },
  {
    id: 'valentine-hearts', en: 'Valentine Hearts', ka: 'ვალენტინობა',
    blurbEn: 'Red hearts drifting over rose paper.',
    blurbKa: 'წითელი გულები ვარდისფერ ქაღალდზე.',
    category: 'wedding', tags: ['valentine', 'hearts', 'love', 'ვალენტინობა', 'გულები', 'სიყვარული'],
    layout: 'card', frame: 'scatter', motif: 'heart', seal: 'red', font: 'pacifico',
    paper: '#fff1f2', ink: '#6a2a34', a1: '#d6455d', a2: '#f08a9b', gold: '#f4c1c8',
    title: { italic: false, weight: 400, size: 'lg' },
  },

  /* ---------------------------------------------------------------- baby */
  {
    id: 'little-prince', en: 'Little Prince', ka: 'პატარა პრინცი',
    blurbEn: 'Baby-blue clouds and a crescent moon for a boy.',
    blurbKa: 'ცისფერი ღრუბლები და ნახევარმთვარე — ბიჭუნასთვის.',
    category: 'baby', tags: ['baby shower', 'boy', 'blue', 'clouds', 'ბეიბი შაუერი', 'ბიჭი', 'ცისფერი'],
    layout: 'card', frame: 'scatter', motif: 'cloud-moon', seal: 'blue', font: 'quicksand',
    paper: '#eef4fb', ink: '#3e5470', a1: '#6f9fd8', a2: '#f2c14e', gold: '#bcd3ee',
    title: { weight: 700, italic: false, size: 'lg' },
  },
  {
    id: 'little-princess', en: 'Little Princess', ka: 'პატარა პრინცესა',
    blurbEn: 'Blossom pink with petals and a soft script for a girl.',
    blurbKa: 'ვარდისფერი ყვავილები და ნაზი ხელნაწერი — გოგონასთვის.',
    category: 'baby', tags: ['baby shower', 'girl', 'pink', 'blossom', 'ბეიბი შაუერი', 'გოგო', 'ვარდისფერი'],
    layout: 'classic', frame: 'corners', motif: 'blossom', seal: 'pink', font: 'parisienne',
    paper: '#fdf2f5', ink: '#6b4a58', a1: '#e58fb0', a2: '#cfae7a', gold: '#f4cbd8',
    title: { italic: false, size: 'lg', lineHeight: 1.3 },
  },
  {
    id: 'gender-reveal', en: 'Gender Reveal', ka: 'ბიჭი თუ გოგო?',
    blurbEn: 'Pink and blue balloons — nobody knows yet.',
    blurbKa: 'ვარდისფერი და ცისფერი ბუშტები — ჯერ არავინ იცის.',
    category: 'baby', tags: ['gender reveal', 'balloons', 'boy or girl', 'ბიჭი', 'გოგო', 'ბუშტები'],
    layout: 'card', frame: 'garland', motif: 'balloons-duo', seal: 'pink', font: 'fredoka',
    paper: '#fbf7f7', ink: '#4a4a4a', a1: '#ec8fb0', a2: '#79b4e8', gold: '#d9d9d9',
    title: { weight: 700, italic: false, size: 'lg' },
  },
  {
    id: 'woodland-baby', en: 'Woodland Baby', ka: 'ტყის ბეიბი შაუერი',
    blurbEn: 'Mushrooms, a fox and forest browns for a neutral shower.',
    blurbKa: 'სოკოები, მელია და ტყის ყავისფერი — ნეიტრალური ბეიბი შაუერი.',
    category: 'baby', tags: ['woodland', 'forest', 'fox', 'neutral', 'ტყე', 'მელია'],
    layout: 'classic', frame: 'corners', motif: 'mushroom-fox', seal: 'gold', font: 'caveat',
    paper: '#f5efe4', ink: '#4a3b2a', a1: '#b07a4a', a2: '#7a9e6b', gold: '#d9b98a',
    title: { weight: 700, italic: false, size: 'xl' },
  },
  {
    id: 'wild-one', en: 'Wild One', ka: 'ველური ერთი',
    blurbEn: 'Boho lion and earthy tones for a first birthday.',
    blurbKa: 'ბოჰო ლომი და მიწისფერი ტონები — პირველი დაბადების დღისთვის.',
    category: 'baby', tags: ['first birthday', 'wild one', 'lion', 'safari', 'პირველი', 'ლომი'],
    layout: 'card', frame: 'edges', motif: 'lion', seal: 'gold', font: 'fredoka',
    paper: '#f6efe1', ink: '#4a3a2a', a1: '#b8863f', a2: '#6f8f5e', gold: '#d9c199',
    title: { weight: 700, italic: false, size: 'lg' },
  },
  {
    id: 'sweet-one', en: 'Sweet One', ka: 'ტკბილი ერთი',
    blurbEn: 'Donuts and pastels for a sweet first birthday.',
    blurbKa: 'დონატები და პასტელი — ტკბილი პირველი დაბადების დღე.',
    category: 'baby', tags: ['first birthday', 'sweet one', 'donut', 'pastel', 'პირველი', 'დონატი'],
    layout: 'card', frame: 'garland', motif: 'donut', seal: 'pink', font: 'pacifico',
    paper: '#fff6f0', ink: '#7a4a5a', a1: '#f29ab3', a2: '#8fd3c9', gold: '#ffd9a8',
    title: { italic: false, weight: 400, size: 'lg' },
  },
  {
    id: 'twinkle-star', en: 'Twinkle Little Star', ka: 'ვარსკვლავო',
    blurbEn: 'Navy night with gold stars — a lullaby of a design.',
    blurbKa: 'ლურჯი ღამე ოქროს ვარსკვლავებით — იავნანასავით ნაზი.',
    category: 'baby', tags: ['stars', 'night', 'twinkle', 'ვარსკვლავები', 'ღამე'],
    layout: 'classic', frame: 'scatter', motif: 'star', seal: 'navy', font: 'quicksand',
    paper: '#1e2a4a', ink: '#f4f0ff', a1: '#f2c14e', a2: '#a9b8e8', gold: '#ffe08a', dark: true,
    title: { weight: 700, italic: false, size: 'lg' },
  },
  {
    id: 'christening', en: 'Christening', ka: 'ნათლობა',
    blurbEn: 'A dove and olive branch on quiet white for baptisms.',
    blurbKa: 'მტრედი და ზეთისხილის რტო წყნარ თეთრზე — ნათლობისთვის.',
    category: 'baby', tags: ['christening', 'baptism', 'dove', 'ნათლობა', 'მტრედი'],
    layout: 'classic', frame: 'corners', motif: 'dove', seal: 'blue', font: 'cormorant',
    paper: '#fbfaf5', ink: '#4a4a4a', a1: '#8fa9c9', a2: '#d4c28a', gold: '#e3d5a3',
    title: { italic: true, weight: 500, size: 'md' },
  },

  /* ----------------------------------------------------------- corporate */
  {
    id: 'office-party', en: 'Office Party', ka: 'ოფისის წვეულება',
    blurbEn: 'Bright blue and amber with confetti — work, but fun.',
    blurbKa: 'ღია ლურჯი და ქარვისფერი კონფეტით — სამსახური, მაგრამ მხიარული.',
    category: 'corporate', tags: ['office', 'team', 'colleagues', 'ოფისი', 'გუნდი', 'კოლეგები'],
    layout: 'card', frame: 'garland', motif: 'briefcase', seal: 'blue', font: 'bebas',
    paper: '#f4f6fa', ink: '#1f2a3a', a1: '#2f6fed', a2: '#ffb020', gold: '#9fc0ff',
    title: { transform: 'uppercase', size: 'xl', italic: false, weight: 400, tracking: '0.05em' },
  },
  {
    id: 'year-end-gala', en: 'Year-End Gala', ka: 'წლის შემაჯამებელი საღამო',
    blurbEn: 'Black tie: champagne gold on midnight.',
    blurbKa: 'შავი ჰალსტუხი: შამპანურის ოქრო შუაღამის ფონზე.',
    category: 'corporate', tags: ['gala', 'new year', 'black tie', 'champagne', 'გალა', 'საახალწლო'],
    layout: 'classic', frame: 'corners', motif: 'champagne', seal: 'gold', font: 'cinzel',
    paper: '#0e1220', ink: '#f3ecd8', a1: '#c9a75a', a2: '#e8d39a', gold: '#dfc27c', dark: true,
    title: { transform: 'uppercase', tracking: '0.12em', weight: 500, italic: false, size: 'md' },
  },
  {
    id: 'product-launch', en: 'Product Launch', ka: 'პროდუქტის პრეზენტაცია',
    blurbEn: 'Bold poster type and a lit bulb — something new is coming.',
    blurbKa: 'მკვეთრი პლაკატური შრიფტი და ანთებული ნათურა — რაღაც ახალი მოდის.',
    category: 'corporate', tags: ['launch', 'product', 'startup', 'presentation', 'პრეზენტაცია', 'სტარტაპი'],
    layout: 'poster', frame: 'edges', motif: 'lightbulb', seal: 'red', font: 'bebas',
    paper: '#f7f8fb', ink: '#14213d', a1: '#ff5c39', a2: '#14213d', gold: '#ffb59f',
    title: { transform: 'uppercase', weight: 400, tracking: '0.01em', lineHeight: 0.86 },
  },
  {
    id: 'summit', en: 'Summit', ka: 'სამიტი',
    blurbEn: 'Cool blues and a globe for conferences and forums.',
    blurbKa: 'ცივი ლურჯი და გლობუსი — კონფერენციებისა და ფორუმებისთვის.',
    category: 'corporate', tags: ['conference', 'summit', 'forum', 'business', 'კონფერენცია', 'ბიზნესი'],
    layout: 'poster', frame: 'corners', motif: 'globe', seal: 'navy', font: 'dmserif-inter',
    paper: '#f3f5f8', ink: '#1e2a3a', a1: '#1f5fa0', a2: '#6d8fb3', gold: '#a7c4e0',
    title: { transform: 'uppercase', weight: 400, tracking: '-0.02em', lineHeight: 0.9 },
  },
  {
    id: 'team-building', en: 'Team Building', ka: 'თიმბილდინგი',
    blurbEn: 'Puzzle pieces in coral and teal — everyone fits.',
    blurbKa: 'პაზლის ნაწილები მარჯნისფერსა და ფირუზისფერში — ყველა ერგება.',
    category: 'corporate', tags: ['team building', 'offsite', 'retreat', 'თიმბილდინგი', 'გუნდი'],
    layout: 'card', frame: 'scatter', motif: 'puzzle', seal: 'gold', font: 'fredoka',
    paper: '#f8f7f2', ink: '#2f2f2f', a1: '#ff7a3d', a2: '#2ab3a6', gold: '#ffd166',
    title: { weight: 700, italic: false, size: 'lg' },
  },
  {
    id: 'awards-night', en: 'Awards Night', ka: 'დაჯილდოების საღამო',
    blurbEn: 'Trophies and spotlight gold on a dark stage.',
    blurbKa: 'თასები და პროჟექტორის ოქრო მუქ სცენაზე.',
    category: 'corporate', tags: ['awards', 'ceremony', 'trophy', 'დაჯილდოება', 'ცერემონია'],
    layout: 'classic', frame: 'corners', motif: 'trophy', seal: 'gold', font: 'abril',
    paper: '#17110f', ink: '#f7ecdc', a1: '#d9a441', a2: '#f2d18a', gold: '#e9c675', dark: true,
    title: { italic: false, weight: 400, size: 'lg' },
  },
  {
    id: 'retirement-toast', en: 'Retirement Toast', ka: 'პენსიაზე გასვლა',
    blurbEn: 'A sailboat heading into calm teal water.',
    blurbKa: 'იალქნიანი ნავი მშვიდ ფირუზისფერ წყალში.',
    category: 'corporate', tags: ['retirement', 'farewell', 'sailboat', 'პენსია', 'გამოსამშვიდობებელი'],
    layout: 'classic', frame: 'edges', motif: 'sailboat', seal: 'blue', font: 'cormorant',
    paper: '#f4f6f4', ink: '#3a4a4a', a1: '#3f7d8c', a2: '#d9a066', gold: '#cfd9c9',
    title: { italic: true, weight: 500, size: 'md' },
  },
  {
    id: 'tech-meetup', en: 'Tech Meetup', ka: 'ტექ შეხვედრა',
    blurbEn: 'Circuit lines in cyan and violet on near-black.',
    blurbKa: 'მიკროსქემის ხაზები ცისფერსა და იისფერში თითქმის შავზე.',
    category: 'corporate', tags: ['tech', 'meetup', 'hackathon', 'developers', 'ტექნოლოგია', 'ჰაკათონი'],
    layout: 'card', frame: 'scatter', motif: 'circuit', seal: 'navy', font: 'orbitron',
    paper: '#0b1220', ink: '#e6f0ff', a1: '#22d3ee', a2: '#a78bfa', gold: '#7dd3fc', dark: true,
    title: { transform: 'uppercase', weight: 700, tracking: '0.06em', italic: false, size: 'md' },
  },
  {
    id: 'charity-gala', en: 'Charity Gala', ka: 'საქველმოქმედო საღამო',
    blurbEn: 'A ribbon heart in crimson and gold for fundraisers.',
    blurbKa: 'ბაფთის გული ალისფერსა და ოქროსფერში — საქველმოქმედო საღამოსთვის.',
    category: 'corporate', tags: ['charity', 'fundraiser', 'benefit', 'ქველმოქმედება', 'ფონდი'],
    layout: 'classic', frame: 'corners', motif: 'ribbon-heart', seal: 'red', font: 'playfair-montserrat',
    paper: '#fbf7f2', ink: '#4a3a3a', a1: '#c0392b', a2: '#d9b36b', gold: '#e6c98c',
    title: { italic: true, weight: 400, size: 'md' },
  },
  {
    id: 'creative-workshop', en: 'Creative Workshop', ka: 'შემოქმედებითი ვორქშოპი',
    blurbEn: 'Violet, amber and lime splashes for workshops and masterclasses.',
    blurbKa: 'იისფერი, ქარვისფერი და ლაიმისფერი შხეფები — ვორქშოპებისთვის.',
    category: 'corporate', tags: ['workshop', 'masterclass', 'creative', 'ვორქშოპი', 'მასტერკლასი'],
    layout: 'card', frame: 'scatter', motif: 'palette', seal: 'pink', font: 'caveat',
    paper: '#fffdf6', ink: '#2a2a2a', a1: '#7c3aed', a2: '#f59e0b', gold: '#a3e635',
    title: { weight: 700, italic: false, size: 'xl' },
  },

  /* ------------------------------------------------------------- holiday */
  {
    id: 'new-year-fireworks', en: 'New Year Fireworks', ka: 'საახალწლო ფეიერვერკი',
    blurbEn: 'Gold and pink fireworks bursting over midnight blue.',
    blurbKa: 'ოქროსფერი და ვარდისფერი ფეიერვერკი შუაღამის ლურჯზე.',
    category: 'holiday', tags: ['new year', 'fireworks', 'countdown', 'ახალი წელი', 'ფეიერვერკი'],
    layout: 'card', frame: 'scatter', motif: 'fireworks', seal: 'gold', font: 'bebas',
    paper: '#0b0f2a', ink: '#f6f1e3', a1: '#f2c14e', a2: '#f25f8a', gold: '#ffe08a', dark: true,
    title: { transform: 'uppercase', size: 'xl', italic: false, weight: 400, tracking: '0.06em' },
  },
  {
    id: 'christmas-pine', en: 'Christmas Pine', ka: 'საშობაო ნაძვი',
    blurbEn: 'Pine branches, red baubles and cream paper.',
    blurbKa: 'ნაძვის ტოტები, წითელი ბურთულები და კრემისფერი ქაღალდი.',
    category: 'holiday', tags: ['christmas', 'pine', 'baubles', 'შობა', 'ნაძვი', 'საახალწლო'],
    layout: 'classic', frame: 'corners', motif: 'pine', seal: 'red', font: 'lobster',
    paper: '#f7f1e6', ink: '#2d4a3a', a1: '#a8322f', a2: '#2e6b45', gold: '#d9b36b',
    title: { italic: false, weight: 400, size: 'lg' },
  },
  {
    id: 'winter-wonderland', en: 'Winter Wonderland', ka: 'ზამთრის ზღაპარი',
    blurbEn: 'Ice blue paper and falling snowflakes.',
    blurbKa: 'ყინულისფერი ქაღალდი და მოთოვლილი ფიფქები.',
    category: 'holiday', tags: ['winter', 'snow', 'snowflake', 'ზამთარი', 'თოვლი', 'ფიფქი'],
    layout: 'classic', frame: 'scatter', motif: 'snowflake', seal: 'blue', font: 'parisienne',
    paper: '#eef4f9', ink: '#2e4a66', a1: '#5e93c9', a2: '#b6d0e8', gold: '#d9e6f2',
    title: { italic: false, size: 'lg', lineHeight: 1.3 },
  },
  {
    id: 'spooky-halloween', en: 'Spooky Halloween', ka: 'ჰელოუინი',
    blurbEn: 'Pumpkins, ghosts and orange on a haunted purple night.',
    blurbKa: 'გოგრები, მოჩვენებები და ნარინჯისფერი მოჩვენებით სავსე იისფერ ღამეში.',
    category: 'holiday', tags: ['halloween', 'pumpkin', 'ghost', 'spooky', 'ჰელოუინი', 'გოგრა'],
    layout: 'card', frame: 'edges', motif: 'pumpkin-ghost', seal: 'navy', font: 'luckiest-guy',
    paper: '#141020', ink: '#fff3d6', a1: '#ff7a1a', a2: '#9b5de5', gold: '#ffcf5c', dark: true,
    title: { italic: false, weight: 400, size: 'lg' },
  },
  {
    id: 'easter-bloom', en: 'Easter Bloom', ka: 'აღდგომა',
    blurbEn: 'Painted eggs and a bunny in spring pastels.',
    blurbKa: 'მოხატული კვერცხები და კურდღელი გაზაფხულის პასტელში.',
    category: 'holiday', tags: ['easter', 'eggs', 'bunny', 'spring', 'აღდგომა', 'კვერცხი'],
    layout: 'card', frame: 'garland', motif: 'egg-bunny', seal: 'pink', font: 'fredoka',
    paper: '#fdf9ee', ink: '#5a4a5a', a1: '#e9a4c4', a2: '#9ad0a8', gold: '#ffe08a',
    title: { weight: 700, italic: false, size: 'lg' },
  },
  {
    id: 'spring-blossom', en: 'Spring Blossom', ka: 'გაზაფხულის ყვავილობა',
    blurbEn: 'Cherry blossom branches in pink and sage.',
    blurbKa: 'ალუბლის ყვავილების ტოტები ვარდისფერსა და ნაცრისფერ-მწვანეში.',
    category: 'holiday', tags: ['spring', 'blossom', 'sakura', 'გაზაფხული', 'ყვავილობა'],
    layout: 'classic', frame: 'corners', motif: 'blossom', seal: 'pink', font: 'great-vibes',
    paper: '#fbf6f4', ink: '#5a4a4a', a1: '#e58fa5', a2: '#8fb8a0', gold: '#f2c9c9',
    title: { italic: false, weight: 400, size: 'lg', lineHeight: 1.3 },
  },
  {
    id: 'summer-splash', en: 'Summer Splash', ka: 'ზაფხულის შხეფები',
    blurbEn: 'Sun, waves and tangerine for pool days.',
    blurbKa: 'მზე, ტალღები და მანდარინისფერი — აუზის დღეებისთვის.',
    category: 'holiday', tags: ['summer', 'pool', 'sun', 'beach', 'ზაფხული', 'აუზი', 'მზე'],
    layout: 'card', frame: 'bottom', motif: 'sun-wave', seal: 'gold', font: 'fredoka',
    paper: '#fff9ea', ink: '#1f4e5f', a1: '#ff9f1c', a2: '#2ec4b6', gold: '#ffe08a',
    title: { weight: 700, italic: false, size: 'lg' },
  },
  {
    id: 'autumn-harvest', en: 'Autumn Harvest', ka: 'შემოდგომის მოსავალი',
    blurbEn: 'Rust leaves and pumpkins on warm oat paper.',
    blurbKa: 'ჟანგისფერი ფოთლები და გოგრები თბილ შვრიისფერ ქაღალდზე.',
    category: 'holiday', tags: ['autumn', 'fall', 'harvest', 'leaves', 'შემოდგომა', 'მოსავალი'],
    layout: 'classic', frame: 'corners', motif: 'autumn-leaf', seal: 'gold', font: 'caveat',
    paper: '#f8efe2', ink: '#5a3a22', a1: '#c2612a', a2: '#8a6a2a', gold: '#e8b35a',
    title: { weight: 700, italic: false, size: 'xl' },
  },
  {
    id: 'womens-day', en: 'Women’s Day', ka: 'ქალთა დღე',
    blurbEn: 'Tulips and mimosa for the 8th of March.',
    blurbKa: 'ტიტები და მიმოზა 8 მარტისთვის.',
    category: 'holiday', tags: ['8 march', 'women', 'tulips', 'mimosa', '8 მარტი', 'ქალები', 'ტიტა'],
    layout: 'classic', frame: 'corners', motif: 'tulip', seal: 'pink', font: 'parisienne',
    paper: '#fff5f7', ink: '#6a3a4a', a1: '#d6457a', a2: '#f2c14e', gold: '#f7d9a8',
    title: { italic: false, size: 'lg', lineHeight: 1.3 },
  },
  {
    id: 'mothers-day', en: 'Mother’s Day', ka: 'დედის დღე',
    blurbEn: 'Peach roses and a soft script for mum.',
    blurbKa: 'ატმისფერი ვარდები და ნაზი ხელნაწერი დედისთვის.',
    category: 'holiday', tags: ['mother', 'mum', 'roses', 'დედა', 'ვარდები'],
    layout: 'classic', frame: 'corners', motif: 'rose', seal: 'pink', font: 'great-vibes',
    paper: '#fdf6f2', ink: '#5a4040', a1: '#d97a8f', a2: '#cdb47a', gold: '#f2c9b8',
    title: { italic: false, weight: 400, size: 'lg', lineHeight: 1.3 },
  },
  {
    id: 'tbilisoba', en: 'Tbilisoba', ka: 'თბილისობა',
    blurbEn: 'Grapes and churchkhela for the city festival.',
    blurbKa: 'ყურძენი და ჩურჩხელა ქალაქის დღესასწაულისთვის.',
    category: 'holiday', tags: ['tbilisi', 'festival', 'churchkhela', 'grapes', 'თბილისი', 'ჩურჩხელა', 'რთველი'],
    layout: 'card', frame: 'edges', motif: 'churchkhela', seal: 'red', font: 'lobster',
    paper: '#fbf3e4', ink: '#4a2a3a', a1: '#7b2d4a', a2: '#d9a441', gold: '#e0c07a',
    title: { italic: false, weight: 400, size: 'lg' },
  },
  {
    id: 'georgian-supra', en: 'Georgian Supra', ka: 'ქართული სუფრა',
    blurbEn: 'Qvevri wine and a toast — the feast table.',
    blurbKa: 'ქვევრის ღვინო და სადღეგრძელო — ქართული სუფრა.',
    category: 'holiday', tags: ['supra', 'feast', 'wine', 'qvevri', 'tamada', 'სუფრა', 'ღვინო', 'ქვევრი', 'თამადა'],
    layout: 'classic', frame: 'corners', motif: 'qvevri', seal: 'red', font: 'playfair-montserrat',
    paper: '#f6efe4', ink: '#3a2a2a', a1: '#8a2f3a', a2: '#b8863f', gold: '#d9b36b',
    title: { italic: true, weight: 400, size: 'md' },
  },

  /* --------------------------------------------------------------- party */
  {
    id: 'red-bows',
    en: 'Red Bows', ka: 'წითელი ბაფთები',
    blurbEn: 'Hand-drawn red bows and streamers over warm ivory.',
    blurbKa: 'ხელნახატი წითელი ბაფთები და სერპანტინი თბილ სპილოსძვლისფერზე.',
    category: 'party', tags: ['bows', 'ribbon', 'red', 'ბაფთები', 'წითელი'],
    layout: 'card', frame: 'none', motif: 'bow', seal: 'red', font: 'playfair-montserrat',
    paper: '#f6ede4', ink: '#ac0000', a1: '#ac0000', a2: '#8a0000', gold: '#c94a4a',
    legacy: true,
    swatch: { paper: '#f6ede4', ink: '#ac0000', accent: '#c94a4a' },
    defaults: { accent: '#ac0000', background: '#f6ede4', font: 'playfair-montserrat' },
    fontHref: 'https://fonts.googleapis.com/css2?family=Pinyon+Script&family=Montserrat:wght@500;600;700&family=Playfair+Display:ital,wght@0,700;1,400&display=swap',
  },
  {
    id: 'cocktail',
    en: 'Cocktail', ka: 'კოქტეილი',
    blurbEn: 'Stone and gold, wide condensed type — for evening parties.',
    blurbKa: 'ქვისფერი და ოქრო, განიერი შრიფტი — საღამოს წვეულებისთვის.',
    category: 'party', tags: ['cocktail', 'evening', 'poster', 'კოქტეილი', 'საღამო'],
    layout: 'poster', frame: 'none', motif: 'wineglass', seal: 'gold', font: 'archivo',
    paper: '#ccc9c1', ink: '#3e3d3f', a1: '#a08a5b', a2: '#3e3d3f', gold: '#c2ab77',
    legacy: true,
    swatch: { paper: '#ccc9c1', ink: '#3e3d3f', accent: '#a08a5b' },
    defaults: { accent: '#a08a5b', background: '#d5d3cb', font: 'archivo' },
    fontHref: 'https://fonts.googleapis.com/css2?family=Archivo:wdth,wght@62..125,100..900&display=swap',
  },
  {
    id: 'wine-and-dine', en: 'Wine & Dine', ka: 'ღვინო და ვახშამი',
    blurbEn: 'Burgundy and gold with a glass raised in each corner.',
    blurbKa: 'ბურგუნდიული და ოქრო, აწეული ჭიქა თითოეულ კუთხეში.',
    category: 'party', tags: ['dinner', 'wine', 'supper', 'ვახშამი', 'ღვინო', 'სადილი'],
    layout: 'classic', frame: 'corners', motif: 'wineglass', seal: 'red', font: 'playfair-montserrat',
    paper: '#f8f3ee', ink: '#4a2a3a', a1: '#7b2d4a', a2: '#c9a24b', gold: '#d9c19a',
    title: { italic: true, weight: 400, size: 'md' },
  },
  {
    id: 'brunch-club', en: 'Brunch Club', ka: 'ბრანჩი',
    blurbEn: 'Croissants and coffee in warm apricot.',
    blurbKa: 'კრუასანები და ყავა თბილ გარგრისფერში.',
    category: 'party', tags: ['brunch', 'breakfast', 'coffee', 'ბრანჩი', 'საუზმე', 'ყავა'],
    layout: 'card', frame: 'garland', motif: 'croissant', seal: 'gold', font: 'pacifico',
    paper: '#fff8ee', ink: '#5a3a2a', a1: '#e07a3f', a2: '#f2c14e', gold: '#ffd9a8',
    title: { italic: false, weight: 400, size: 'lg' },
  },
  {
    id: 'bbq-cookout', en: 'BBQ Cookout', ka: 'მწვადი',
    blurbEn: 'Grill flames and smoky red for a backyard cookout.',
    blurbKa: 'მაყალის ცეცხლი და მოწითალო ფერები ეზოში მწვადისთვის.',
    category: 'party', tags: ['bbq', 'barbecue', 'grill', 'mtsvadi', 'მწვადი', 'მაყალი', 'ბარბექიუ'],
    layout: 'card', frame: 'edges', motif: 'grill', seal: 'red', font: 'bangers',
    paper: '#f7efe3', ink: '#3a2a1e', a1: '#c0392b', a2: '#e67e22', gold: '#f0c75e',
    title: { transform: 'uppercase', size: 'xl', italic: false, weight: 400, tracking: '0.03em' },
  },
  {
    id: 'karaoke-night', en: 'Karaoke Night', ka: 'კარაოკე',
    blurbEn: 'Magenta stage lights and a microphone on deep purple.',
    blurbKa: 'მაგენტა სცენის შუქები და მიკროფონი მუქ იისფერზე.',
    category: 'party', tags: ['karaoke', 'music', 'sing', 'კარაოკე', 'მუსიკა', 'სიმღერა'],
    layout: 'poster', frame: 'scatter', motif: 'mic', seal: 'pink', font: 'bebas',
    paper: '#1a0f2e', ink: '#fff0f8', a1: '#ff4fd8', a2: '#ffd166', gold: '#c084fc', dark: true,
    title: { transform: 'uppercase', weight: 400, tracking: '0.01em', lineHeight: 0.86 },
  },
  {
    id: 'movie-night', en: 'Movie Night', ka: 'კინოს საღამო',
    blurbEn: 'Popcorn and a red-velvet curtain feel.',
    blurbKa: 'პოპკორნი და წითელი ხავერდის ფარდის განწყობა.',
    category: 'party', tags: ['movie', 'cinema', 'film', 'popcorn', 'კინო', 'ფილმი'],
    layout: 'poster', frame: 'corners', motif: 'popcorn', seal: 'red', font: 'abril',
    paper: '#100e0e', ink: '#f7efe0', a1: '#e63946', a2: '#f1c453', gold: '#f4d58d', dark: true,
    title: { transform: 'uppercase', weight: 400, tracking: '-0.02em', lineHeight: 0.9 },
  },
  {
    id: 'game-night', en: 'Game Night', ka: 'სამაგიდო თამაშები',
    blurbEn: 'Dice and cards in red and teal for board-game nights.',
    blurbKa: 'კამათელი და კარტი წითელსა და ფირუზისფერში — სამაგიდო თამაშების საღამო.',
    category: 'party', tags: ['games', 'board games', 'dice', 'თამაშები', 'კამათელი'],
    layout: 'card', frame: 'scatter', motif: 'dice', seal: 'navy', font: 'fredoka',
    paper: '#f6f3ee', ink: '#2a2a3a', a1: '#e63946', a2: '#2a9d8f', gold: '#f4a261',
    title: { weight: 700, italic: false, size: 'lg' },
  },
  {
    id: 'masquerade-ball', en: 'Masquerade Ball', ka: 'მასკარადი',
    blurbEn: 'Venetian masks in amethyst and gold on velvet black.',
    blurbKa: 'ვენეციური ნიღბები ამეთვისტოსფერსა და ოქროსფერში ხავერდის შავზე.',
    category: 'party', tags: ['masquerade', 'mask', 'ball', 'მასკარადი', 'ნიღაბი'],
    layout: 'classic', frame: 'corners', motif: 'mask', seal: 'navy', font: 'cinzel',
    paper: '#150f1e', ink: '#f3e9d2', a1: '#b08cd9', a2: '#d4af37', gold: '#e2c66e', dark: true,
    title: { transform: 'uppercase', tracking: '0.1em', weight: 500, italic: false, size: 'md' },
  },
  {
    id: 'casino-night', en: 'Casino Night', ka: 'კაზინოს საღამო',
    blurbEn: 'Cards and chips, green felt and gold.',
    blurbKa: 'კარტი და ჩიპები, მწვანე ხავერდი და ოქრო.',
    category: 'party', tags: ['casino', 'poker', 'cards', 'vegas', 'კაზინო', 'პოკერი'],
    layout: 'card', frame: 'scatter', motif: 'cards', seal: 'gold', font: 'abril',
    paper: '#0f1a14', ink: '#f6efd8', a1: '#d4af37', a2: '#c0392b', gold: '#e6cf8b', dark: true,
    title: { italic: false, weight: 400, size: 'lg' },
  },
  {
    id: 'housewarming', en: 'Housewarming', ka: 'ბინის დალოცვა',
    blurbEn: 'A little house and a key on warm oat paper.',
    blurbKa: 'პატარა სახლი და გასაღები თბილ შვრიისფერ ქაღალდზე.',
    category: 'party', tags: ['housewarming', 'new home', 'house', 'ბინა', 'სახლი', 'დალოცვა'],
    layout: 'classic', frame: 'corners', motif: 'house', seal: 'gold', font: 'caveat',
    paper: '#f9f5ec', ink: '#4a3a2a', a1: '#c97b3a', a2: '#7a9e6b', gold: '#e2c49b',
    title: { weight: 700, italic: false, size: 'xl' },
  },
  {
    id: 'garden-tea-party', en: 'Garden Tea Party', ka: 'ჩაის წვეულება',
    blurbEn: 'Teacups and roses for an afternoon in the garden.',
    blurbKa: 'ჩაის ფინჯნები და ვარდები — შუადღე ბაღში.',
    category: 'party', tags: ['tea', 'afternoon', 'garden', 'bridal shower', 'ჩაი', 'ბაღი'],
    layout: 'classic', frame: 'corners', motif: 'teacup', seal: 'pink', font: 'parisienne',
    paper: '#f6f8f1', ink: '#4a5a4a', a1: '#d98aa8', a2: '#7fa895', gold: '#d9c9a3',
    title: { italic: false, size: 'lg', lineHeight: 1.3 },
  },

  /* ------------------------------------------------------------- outdoor */
  {
    id: 'mountain-trip', en: 'Mountain Trip', ka: 'მთის მოგზაურობა',
    blurbEn: 'Pine green peaks and bold type for a trip to the mountains.',
    blurbKa: 'ფიჭვისფერი მწვერვალები და მკვეთრი შრიფტი — მთაში მოგზაურობისთვის.',
    category: 'outdoor', tags: ['mountain', 'trip', 'hiking', 'kazbegi', 'მთა', 'ლაშქრობა', 'ყაზბეგი'],
    layout: 'card', frame: 'edges', motif: 'mountain', seal: 'sage', font: 'bebas',
    paper: '#eef3f0', ink: '#2a3a3a', a1: '#2f6f5f', a2: '#d9a066', gold: '#b9d3c3',
    title: { transform: 'uppercase', size: 'xl', italic: false, weight: 400, tracking: '0.05em' },
  },
  {
    id: 'camping-under-stars', en: 'Camping Under Stars', ka: 'კემპინგი',
    blurbEn: 'A tent and campfire under a navy night sky.',
    blurbKa: 'კარავი და კოცონი ლურჯი ღამის ცის ქვეშ.',
    category: 'outdoor', tags: ['camping', 'tent', 'campfire', 'stars', 'კემპინგი', 'კარავი', 'კოცონი'],
    layout: 'classic', frame: 'edges', motif: 'tent', seal: 'navy', font: 'caveat',
    paper: '#101a2a', ink: '#f2ead8', a1: '#f2a541', a2: '#8ab4c9', gold: '#ffe08a', dark: true,
    title: { weight: 700, italic: false, size: 'xl' },
  },
  {
    id: 'beach-day', en: 'Beach Day', ka: 'სანაპირო',
    blurbEn: 'Striped umbrellas and tangerine sun along the shore.',
    blurbKa: 'ზოლიანი ქოლგები და მანდარინისფერი მზე სანაპიროზე.',
    category: 'outdoor', tags: ['beach', 'sea', 'summer', 'umbrella', 'სანაპირო', 'ზღვა', 'ზაფხული'],
    layout: 'card', frame: 'bottom', motif: 'umbrella', seal: 'gold', font: 'pacifico',
    paper: '#fff9ec', ink: '#2e4a55', a1: '#ff8c42', a2: '#2aa7b8', gold: '#ffd97a',
    title: { italic: false, weight: 400, size: 'lg' },
  },
  {
    id: 'garden-party', en: 'Garden Party', ka: 'ბაღის წვეულება',
    blurbEn: 'Watering cans and blooms in fresh green and pink.',
    blurbKa: 'სარწყავები და ყვავილები ცოცხალ მწვანესა და ვარდისფერში.',
    category: 'outdoor', tags: ['garden', 'flowers', 'spring', 'ბაღი', 'ყვავილები'],
    layout: 'classic', frame: 'corners', motif: 'watering-can', seal: 'sage', font: 'great-vibes',
    paper: '#f5f8ee', ink: '#3f5a3a', a1: '#7fa851', a2: '#e8a0b8', gold: '#f2d16b',
    title: { italic: false, weight: 400, size: 'lg', lineHeight: 1.3 },
  },
  {
    id: 'picnic', en: 'Picnic', ka: 'პიკნიკი',
    blurbEn: 'A gingham basket and apples for a day in the park.',
    blurbKa: 'კალათა და ვაშლები — დღე პარკში.',
    category: 'outdoor', tags: ['picnic', 'park', 'gingham', 'basket', 'პიკნიკი', 'პარკი'],
    layout: 'card', frame: 'corners', motif: 'basket', seal: 'red', font: 'chewy',
    paper: '#fbf7ee', ink: '#5a2a2a', a1: '#c0392b', a2: '#6f8f4e', gold: '#f2c14e',
    title: { italic: false, weight: 400, size: 'lg' },
  },

  /* ------------------------------------------------------------- elegant */
  {
    id: 'midnight-noir', en: 'Midnight Noir', ka: 'შუაღამის ნუარი',
    blurbEn: 'Near-black paper, bone-white serif, a thin crescent moon.',
    blurbKa: 'თითქმის შავი ქაღალდი, ძვლისფერი სერიფი და თხელი ნახევარმთვარე.',
    category: 'elegant', tags: ['noir', 'black', 'minimal', 'dark', 'ნუარი', 'შავი', 'მინიმალისტური'],
    layout: 'poster', frame: 'corners', motif: 'moon-line', seal: 'navy', font: 'dmserif-inter',
    paper: '#0c0c0f', ink: '#f2f0ec', a1: '#d9d4c7', a2: '#8a8580', gold: '#bdb7aa', dark: true,
    title: { transform: 'uppercase', weight: 400, tracking: '-0.02em', lineHeight: 0.9 },
  },
  {
    id: 'marble-minimal', en: 'Marble Minimal', ka: 'მარმარილო',
    blurbEn: 'Warm white, spaced capitals and no ornament at all.',
    blurbKa: 'თბილი თეთრი, დაშორებული მთავრული ასოები და არანაირი ორნამენტი.',
    category: 'elegant', tags: ['marble', 'minimal', 'white', 'clean', 'მარმარილო', 'მინიმალისტური'],
    layout: 'classic', frame: 'none', motif: 'none', seal: 'gold', font: 'cinzel',
    paper: '#f7f6f4', ink: '#2a2a2a', a1: '#7a7a7a', a2: '#b8a988', gold: '#cfc4ad', titleColor: '#2a2a2a',
    title: { transform: 'uppercase', tracking: '0.16em', weight: 400, italic: false, size: 'sm' },
  },
  {
    id: 'pearl-coquette', en: 'Pearl & Bows', ka: 'მარგალიტი და ბაფთები',
    blurbEn: 'Satin bows and pearls in powder pink — the coquette look.',
    blurbKa: 'ატლასის ბაფთები და მარგალიტები პუდრისფერ ვარდისფერში.',
    category: 'elegant', tags: ['coquette', 'bows', 'pearls', 'pink', 'bridal', 'ბაფთები', 'მარგალიტი'],
    layout: 'classic', frame: 'corners', motif: 'bow', seal: 'pink', font: 'parisienne',
    paper: '#fff6f8', ink: '#6a4a58', a1: '#e8a0b8', a2: '#d9c2a3', gold: '#f2d9c0',
    title: { italic: false, size: 'lg', lineHeight: 1.3 },
  },
]

/** Every template except 'auto' has a designed look on the invitation. */
export const INVITE_TEMPLATES: InviteTemplate[] = [
  {
    id: 'auto',
    label: { en: 'Auto — match event type', ka: 'ავტო — ღონისძიების ტიპის მიხედვით' },
    blurb: {
      en: 'The classic look, tinted to the kind of event you picked.',
      ka: 'კლასიკური იერსახე, შერჩეული ღონისძიების ტიპის მიხედვით.',
    },
    category: 'elegant',
    tags: ['auto', 'classic', 'default', 'ავტო', 'კლასიკური'],
    swatch: { paper: '#eaf0ec', ink: '#41544c', accent: '#7fa895' },
    defaults: { accent: '#7fa895', background: '#eaf0ec', font: 'cormorant' },
    layout: 'classic',
    frame: 'none',
    motif: 'none',
    seal: 'sage',
    palette: {
      paper: '#eaf0ec', card: '#f9fbf8', ink: '#41544c', muted: '#8ba296', a1: '#7fa895',
      a2: '#d8a48f', gold: '#a5b89b', line: '#d3e0d7', title: '#b97f65', btnHover: '#c88f75',
      btnOff: '#ecd3c6',
    },
    dark: false,
    title: {},
    legacy: true,
    collection: 'classic',
  },
  ...POPUP_DEFS.map(buildPainted),
  ...PAINTED_DEFS.map(buildPainted),
  ...DEFS.map(build),
]

const TEMPLATE_IDS = INVITE_TEMPLATES.map((tpl) => tpl.id)

/**
 * The collections the website offers. The classic one is built and kept but
 * unlisted: nothing below advertises it, yet every template still resolves by
 * id, so an event already wearing a classic design keeps rendering. 'auto' is
 * the default look rather than a design, and is always offered.
 */
const LISTED_COLLECTIONS: TemplateCollection[] = ['painted']

export const isListed = (tpl: InviteTemplate) => tpl.id === 'auto' || LISTED_COLLECTIONS.includes(tpl.collection)

/** The designed templates the website offers — everything listed but 'auto'. */
export const DESIGNED_TEMPLATES = INVITE_TEMPLATES.filter((tpl) => tpl.id !== 'auto' && isListed(tpl))

export const cleanTemplateId = (value?: string | null) =>
  value && TEMPLATE_IDS.includes(value) ? value : 'auto'

export const getTemplate = (id?: string | null) =>
  INVITE_TEMPLATES.find((tpl) => tpl.id === cleanTemplateId(id)) as InviteTemplate

/**
 * Load a template's webfonts on demand. The picker calls this for every card
 * it shows; the invitation calls it for the one template it renders. Repeat
 * calls for the same href are no-ops.
 */
export const loadTemplateFonts = (id?: string | null) => {
  const href = getTemplate(id).fontHref
  if (!href || typeof document === 'undefined') return
  if (document.querySelector(`link[data-inv-tpl="${href}"]`)) return
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = href
  link.dataset.invTpl = href
  document.head.appendChild(link)
}

/* ----------------------------------------------------- search & grouping */

const norm = (s: string) => s.toLowerCase().trim()

/**
 * Templates matching a free-text query (any word in either language) inside an
 * optional category. An empty query returns the whole category.
 */
export const searchTemplates = (query: string, category?: TemplateCategory | 'all') => {
  const q = norm(query || '')
  return INVITE_TEMPLATES.filter((tpl) => {
    if (!isListed(tpl)) return false
    if (category && category !== 'all' && tpl.category !== category) return false
    if (!q) return true
    const hay = [
      tpl.id,
      tpl.label.en,
      tpl.label.ka,
      tpl.blurb.en,
      tpl.blurb.ka,
      ...tpl.tags,
      categoryLabel(tpl.category, 'en'),
      categoryLabel(tpl.category, 'ka'),
    ]
      .join(' ')
      .toLowerCase()
    return q.split(/\s+/).every((word) => hay.includes(word))
  })
}

export const templatesInCategory = (category: TemplateCategory) =>
  DESIGNED_TEMPLATES.filter((tpl) => tpl.category === category)

export const PAINTED_TEMPLATES = DESIGNED_TEMPLATES.filter((tpl) => tpl.collection === 'painted')
export const CLASSIC_TEMPLATES = DESIGNED_TEMPLATES.filter((tpl) => tpl.collection === 'classic')

/**
 * The picker's and the gallery's filter: free text and category as in
 * searchTemplates, narrowed to a collection and, inside the painted one, to a
 * look. 'auto' only ever shows in the unfiltered classic/all lists.
 */
export const filterTemplates = (opts: {
  query?: string
  category?: TemplateCategory | 'all'
  collection?: TemplateCollection | 'all'
  look?: PaintedLook | 'all'
}) => {
  const narrowed = !!opts.query || (opts.category && opts.category !== 'all') || (opts.look && opts.look !== 'all')
  return searchTemplates(opts.query || '', opts.category).filter((tpl) => {
    if (tpl.id === 'auto') return !narrowed && opts.collection !== 'painted'
    if (opts.collection && opts.collection !== 'all' && tpl.collection !== opts.collection) return false
    if (opts.look && opts.look !== 'all' && tpl.painted?.look !== opts.look) return false
    return true
  })
}

/* ------------------------------------------------------- the inline look */

// Title sizes per key: desktop, then the ≤880px phone value.
const TITLE_SIZES: Record<NonNullable<TitleStyle['size']>, [string, string]> = {
  sm: ['clamp(2.2rem, 3.6vw, 3.2rem)', 'clamp(2rem, 9vw, 3rem)'],
  md: ['clamp(2.6rem, 4.6vw, 4rem)', 'clamp(2.2rem, 10.5vw, 3.6rem)'],
  lg: ['clamp(3rem, 6vw, 5rem)', 'clamp(2.5rem, 12vw, 4.2rem)'],
  xl: ['clamp(3.4rem, 7.6vw, 6.5rem)', 'clamp(2.6rem, 13vw, 4.4rem)'],
}

/**
 * The inline custom properties and classes that dress the invitation in a
 * data-driven template. Legacy designs return nothing — their CSS owns them.
 *
 * The classes reuse the host-override machinery on purpose: `inv-bg-custom`
 * paints flat paper with accent glows and `inv-bg-dark` flips the translucent
 * surfaces, both proven on every colour a host can pick. The host's own
 * overrides (buildInviteLook) are merged AFTER this style, so they still win.
 */
export const buildTemplateLook = (tpl: InviteTemplate) => {
  const classes = ['inv-tpl', `inv-lay-${tpl.layout}`, `inv-frame-${tpl.frame}`]
  if (tpl.legacy) {
    return { style: {} as CSSProperties, className: classes.join(' ') }
  }
  const p = tpl.palette
  const font = getInviteFont(tpl.defaults.font)
  const style: Record<string, string> = {
    '--inv-paper': p.paper,
    '--inv-card': p.card,
    '--inv-ink': p.ink,
    '--inv-muted': p.muted,
    '--inv-a1': p.a1,
    '--inv-a2': p.a2,
    '--inv-gold': p.gold,
    '--inv-line': p.line,
    '--inv-title': p.title,
    '--inv-btn': p.a1,
    '--inv-btn-hover': p.btnHover,
    '--inv-btn-off': p.btnOff,
  }
  if (font) {
    style['--inv-display'] = font.display
    style['--inv-serif'] = font.display
    style['--inv-body'] = font.body
    style['--inv-ui'] = font.ui || font.body
    style['--inv-ka'] = kaTail(font.body)
  }
  const t = tpl.title
  if (t.transform) style['--inv-title-transform'] = t.transform
  if (t.weight) style['--inv-title-weight'] = String(t.weight)
  if (t.italic !== undefined) style['--inv-title-style'] = t.italic ? 'italic' : 'normal'
  if (t.tracking) style['--inv-title-tracking'] = t.tracking
  if (t.rotate) style['--inv-title-rotate'] = `${t.rotate}deg`
  if (t.lineHeight) style['--inv-title-lh'] = String(t.lineHeight)
  if (t.size) {
    style['--inv-title-size'] = TITLE_SIZES[t.size][0]
    style['--inv-title-size-m'] = TITLE_SIZES[t.size][1]
  }
  classes.push('inv-bg-custom', 'inv-accent-custom')
  if (tpl.dark || isDarkColor(p.paper)) classes.push('inv-bg-dark')
  // Solid buttons hardcode white labels; once the accent is light enough
  // that white sinks into it, the labels go dark (same rule as host overrides).
  if (contrastRatio(p.a1, '#ffffff') < 2.6) classes.push('inv-btn-ink-dark')
  return { style: style as CSSProperties, className: classes.join(' ') }
}

/* ------------------------------------------------------- sample content */

type SampleEvent = {
  event_type: string
  title: string
  honoree_name: string
  host_name: string
  location: string
  description: string
  dress_code: string
  itinerary: Array<{ time: string; title: string; where: string }>
}

/**
 * The stand-in event the /templates/:id preview and the thumbnails show, so a
 * wedding template previews a wedding and a corporate one a gala. Event types
 * are the stored English labels from lib/eventTypes.ts.
 */
export const sampleEventFor = (category: TemplateCategory, lang: string): SampleEvent => {
  const ka = lang === 'ka'
  const rows = (a: [string, string, string], b: [string, string, string], c: [string, string, string]) =>
    [a, b, c].map(([time, title, where]) => ({ time, title, where }))
  switch (category) {
    case 'kids':
      return {
        event_type: ka ? 'დაბადების დღე' : 'Birthday Party',
        title: ka ? 'ლეო 5 წლის ხდება' : 'Leo turns 5',
        honoree_name: ka ? 'ლეო' : 'Leo',
        host_name: ka ? 'ნინო და დათო' : 'Nino & Dato',
        location: ka ? 'მზიური, თბილისი' : 'Mziuri Park, Tbilisi',
        description: ka ? 'თამაშები, ტორტი და ბევრი სიცილი — მოდი და იზეიმე ჩვენთან ერთად!' : 'Games, cake and lots of laughter — come and celebrate with us!',
        dress_code: ka ? 'თავისუფალი' : 'Come as you are',
        itinerary: rows(['15:00', ka ? 'თამაშები' : 'Games', ka ? 'ბაღი' : 'Garden'], ['16:30', ka ? 'ტორტი' : 'Cake', ''], ['17:30', ka ? 'საჩუქრები' : 'Presents', '']),
      }
    case 'wedding':
      return {
        event_type: ka ? 'ქორწილი' : 'Wedding',
        title: ka ? 'ნინო & ლუკა' : 'Nino & Luka',
        honoree_name: ka ? 'ნინო და ლუკა' : 'Nino & Luka',
        host_name: ka ? 'ნინო და ლუკა' : 'Nino & Luka',
        location: ka ? 'ჭიათურის სასახლე, თბილისი' : 'The Old Winery, Tbilisi',
        description: ka ? 'გვსურს, ჩვენი ბედნიერი დღე თქვენთან ერთად აღვნიშნოთ.' : 'We would love you to share our day with us.',
        dress_code: ka ? 'საზეიმო' : 'Black tie optional',
        itinerary: rows(['16:00', ka ? 'ცერემონია' : 'Ceremony', ka ? 'ბაღი' : 'Garden'], ['18:00', ka ? 'ვახშამი' : 'Dinner', ka ? 'დარბაზი' : 'Main hall'], ['21:00', ka ? 'ცეკვები' : 'Dancing', ''])
      }
    case 'baby':
      return {
        event_type: ka ? 'ბეიბი შაუერი' : 'Baby Shower',
        title: ka ? 'პატარა მარიამი' : 'Baby Mariam',
        honoree_name: ka ? 'მარიამი' : 'Mariam',
        host_name: ka ? 'ანა' : 'Ana',
        location: ka ? 'ვაკის პარკი, თბილისი' : 'Vake Park, Tbilisi',
        description: ka ? 'შემოგვიერთდით პატარას მოლოდინში — ჩაი, ნამცხვარი და თამაშები.' : 'Join us as we wait for the little one — tea, cake and games.',
        dress_code: ka ? 'პასტელური ფერები' : 'Pastels',
        itinerary: rows(['14:00', ka ? 'მიღება' : 'Welcome', ''], ['15:00', ka ? 'თამაშები' : 'Games', ''], ['16:00', ka ? 'საჩუქრები' : 'Gifts', '']),
      }
    case 'corporate':
      return {
        event_type: ka ? 'კორპორატიული ღონისძიება' : 'Corporate Event',
        title: ka ? 'წლის შემაჯამებელი საღამო' : 'Year-End Gala',
        honoree_name: '',
        host_name: ka ? 'სთორიფაი' : 'Storyfy',
        location: ka ? 'სასტუმრო ბილტმორი, თბილისი' : 'The Biltmore, Tbilisi',
        description: ka ? 'ერთად შევაჯამოთ წელი — ჯილდოები, ვახშამი და მუსიკა.' : 'Let’s close the year together — awards, dinner and music.',
        dress_code: ka ? 'საქმიანი' : 'Business formal',
        itinerary: rows(['19:00', ka ? 'მიღება' : 'Reception', ka ? 'ფოიე' : 'Lobby'], ['20:00', ka ? 'დაჯილდოება' : 'Awards', ka ? 'დარბაზი' : 'Ballroom'], ['21:30', ka ? 'ვახშამი' : 'Dinner', '']),
      }
    case 'holiday':
      return {
        event_type: ka ? 'დღესასწაული' : 'Holiday Celebration',
        title: ka ? 'სადღესასწაულო წვეულება' : 'Holiday Party',
        honoree_name: '',
        host_name: ka ? 'ანა' : 'Ana',
        location: ka ? 'რუსთაველის 12, თბილისი' : '12 Rustaveli Ave, Tbilisi',
        description: ka ? 'მოდი და აღვნიშნოთ ერთად — სუფრა, მუსიკა და კარგი განწყობა.' : 'Come celebrate with us — food, music and good cheer.',
        dress_code: ka ? 'სადღესასწაულო' : 'Festive',
        itinerary: rows(['18:00', ka ? 'მიღება' : 'Welcome', ''], ['19:30', ka ? 'ვახშამი' : 'Dinner', ''], ['22:00', ka ? 'ცეკვები' : 'Dancing', '']),
      }
    case 'party':
      return {
        event_type: ka ? 'სადილი' : 'Dinner Party',
        title: ka ? 'პარასკევის საღამო' : 'Friday Night',
        honoree_name: '',
        host_name: ka ? 'გიორგი' : 'Giorgi',
        location: ka ? 'ფაბრიკა, თბილისი' : 'Fabrika, Tbilisi',
        description: ka ? 'კარგი კომპანია, კარგი მუსიკა — მოდი!' : 'Good company, good music — come along!',
        dress_code: ka ? 'თავისუფალი' : 'Smart casual',
        itinerary: rows(['20:00', ka ? 'კოქტეილები' : 'Cocktails', ka ? 'ტერასა' : 'Terrace'], ['21:00', ka ? 'ვახშამი' : 'Dinner', ''], ['23:00', ka ? 'ცეკვები' : 'Dancing', '']),
      }
    case 'outdoor':
      return {
        event_type: ka ? 'მოგზაურობა' : 'Trip',
        title: ka ? 'შაბათ-კვირა ბუნებაში' : 'Weekend Away',
        honoree_name: '',
        host_name: ka ? 'ნიკა' : 'Nika',
        location: ka ? 'ყაზბეგი' : 'Kazbegi',
        description: ka ? 'ორი დღე მთაში — ლაშქრობა, კოცონი და ვარსკვლავები.' : 'Two days in the mountains — hiking, campfire and stars.',
        dress_code: ka ? 'თბილად ჩაიცვი' : 'Dress warm',
        itinerary: rows(['09:00', ka ? 'გასვლა' : 'Departure', ka ? 'თბილისი' : 'Tbilisi'], ['13:00', ka ? 'ლაშქრობა' : 'Hike', ''], ['20:00', ka ? 'კოცონი' : 'Campfire', '']),
      }
    case 'elegant':
      return {
        event_type: ka ? 'სადილი' : 'Dinner Party',
        title: ka ? 'ვახშამი რვაზე' : 'Dinner at Eight',
        honoree_name: '',
        host_name: ka ? 'ელენე' : 'Elene',
        location: ka ? 'სტამბა, თბილისი' : 'Stamba, Tbilisi',
        description: ka ? 'კამერული ვახშამი ახლო მეგობრებთან ერთად.' : 'An intimate dinner with close friends.',
        dress_code: ka ? 'ელეგანტური' : 'Cocktail attire',
        itinerary: rows(['20:00', ka ? 'აპერიტივი' : 'Aperitif', ''], ['20:30', ka ? 'ვახშამი' : 'Dinner', ''], ['22:30', ka ? 'დესერტი' : 'Dessert', '']),
      }
    case 'birthday':
    default:
      return {
        event_type: ka ? 'დაბადების დღე' : 'Birthday Party',
        title: ka ? 'ანას დაბადების დღე' : 'Anna’s Birthday Party',
        honoree_name: ka ? 'ანა' : 'Anna',
        host_name: ka ? 'ანა' : 'Anna',
        location: ka ? 'კიკეთის ფერმა' : 'The Old Winery, Tbilisi',
        description: ka ? 'შემოგვიერთდი საღამოს, რომელიც დიდხანს გვემახსოვრება.' : 'Come celebrate with us — dinner, cake and dancing until late.',
        dress_code: ka ? 'ელეგანტური' : 'Smart casual',
        itinerary: rows(['19:00', ka ? 'სტუმრების მიღება' : 'Welcome drinks', ka ? 'ტერასა' : 'Terrace'], ['20:00', ka ? 'სადილი და სადღეგრძელოები' : 'Dinner & toasts', ka ? 'დარბაზი' : 'Main hall'], ['22:00', ka ? 'ტორტი და ცეკვები' : 'Cake & dancing', '']),
      }
  }
}

/** Short title shown on the static thumbnails, per category. */
export const sampleTitleFor = (category: TemplateCategory, lang: string) =>
  sampleEventFor(category, lang).title

// Keep the registry honest at build time: every template's font must exist.
if (process.env.NODE_ENV !== 'production') {
  INVITE_TEMPLATES.forEach((tpl) => {
    if (!INVITE_FONTS.some((f) => f.id === tpl.defaults.font)) {
      // eslint-disable-next-line no-console
      console.warn(`inviteTemplates: ${tpl.id} points at unknown font ${tpl.defaults.font}`)
    }
  })
}
